import React, {
	useEffect,
	useRef,
	useState,
} from 'react';

import {
	defaultSorter,
	visualContentLoopProviderSorter,
} from '../helper/Brood.js';

import {
	broncoURLENV,
} from '../ENV';

import '../styles/custom/brood.css';
// import 'animate.css/animate.css';

import './Brood.new.css';

// providers
import PelicanProviderComponent from './Pelican.js';
import NovaProviderComponent from './Nova.js';
import PeacockProviderComponent from './Peacock.js';
import MonarchProviderComponent from './Monarch.js';
// import BroadsignProviderComponent from './BroadsignFeed.js';

// slide types
import BroodImageSlide from './BroodImageSlide.js';
import BroodVideoSlide from './BroodVideoSlide.js';
import BroodHTMLSlide from './BroodHTMLSlide.js';

const broodPOPSessionKey = 'brood_pop_queue';
const broodPOPQueueMaxAgeMs = 1000 * 60 * 60;
// Hard cap per queue bucket. Per-play keys mean an extended outage adds one
// entry per rotation; without a cap sessionStorage eventually overflows and
// every setItem stringifies the whole queue (DEV-11946).
const broodPOPQueueMaxEntries = 500;
// Vistar/Nova PoP-response metrics relayed from /nova_pop | /peacock_pop into
// the /brood/pop record (DEV-11958). Whitelist merge — the provider response
// must not overwrite play fields like duration or start_of_play.
const broodPOPMetricFields = [
	'pop_impressions',
	'pop_media_cost',
	'pop_spots',
	'pop_expires',
	'pop_errors',
	'pop_message',
	'pop_http_status',
];

window._broodProofOfPlay = window._broodProofOfPlay || {};

class BroodComponent extends React.Component {
	constructor(props) {
		super(props);

		this.debug = false;

		this.playing = false;
		this.updated = false;
		this.isTransitioning = false;
		this.transitionStartTime = null;

		let transitionEnabled = props.brood_enable_transition;
		if (transitionEnabled !== false) {
			transitionEnabled = true;
		}

		let transitionDuration = 1;
		if (transitionEnabled) {
			transitionDuration = 1000;
		}

		this.broodID = `BROOD_${this.props.component_name}`;

		this.state = {
			transitionEnabled,
			transitionDuration,
			status: {
				playing:        false,
				slideLoopCount: 0,
			},

			providers:                  [],
			slides:                     [],
			currSlideID:                false,
			nextSlideID:                false,
			// Slide that just left `curr` on a successful advance. Kept mounted
			// (even if the provider skipper now rejects it — Pelican folder
			// cursor) for transitionDuration so it can fade out. (DEV-12201)
			prevSlideID:                false,
			prevSlide:                  false,
			isLastProviderSlide:        false,
			providerPlays:              {}, // Tracks successfully played slideIDs for each provider as Sets
			providerAttempts:           {}, // Tracks all attempted slideIDs for each provider as Sets (success, error, skip)
			providerSlideIndex:         {}, // Tracks the last played slide index for each provider
			visualContentLoopName:      '',
			visualContentLoopProviders: [], // Visual content loop providers fetched from API
			visualContentLoopProvidersRemoved: [], // Visual content loop roviders that were removed
			visualContentLoopLoopPlayCount: 0, // tracks current loop count for indexing when a slide plays in what loop
			visualContentLoopMissingSlideCounts: {
				0: {},
			},
			visualContentLoopErrorSlideCounts: {
				0: 0,
			},
			visualContentLoopPlayedSlideCounts: {
				0: {},
			},
		};

		if (!window._brood) {
			window._brood = {};
		}
		window._brood[this.broodID] = this;
		this.currentPoPQueue = JSON.parse(sessionStorage.getItem(broodPOPSessionKey) || '{}');

		// Media backoff system for failed slides (in-memory only)
		this.backoffSlides = {};

		// Debug overlay state
		this.showDebugOverlay = this.checkDebugFromHash(); // Check URL hash for debug flag
		this.debugCountdownTimer = null;
		this.debugElapsed = 0;
		this.debugCountdown = 0;
		this.debugPausedTime = 0;
		this.debugActualDuration = 0;
		this.currentSlideStartTime = null;
		this.pausedAt = null; // Track when we paused
		this.totalPausedTime = 0; // Total time spent paused for current slide

		this.providerSorter = defaultSorter;
		if (props.is_visual_content_loop) {
			this.providerSorter = visualContentLoopProviderSorter;
		}

		this.waitingForExpectedFirstProvider = true;
	}

	// Helper method to check URL hash for debug parameter
	checkDebugFromHash() {
		try {
			const hash = window.location.hash;
			if (!hash) return false;

			// Remove the # and split by &
			const params = hash.slice(1).split('&');

			for (const param of params) {
				const [ key, value, ] = param.split('=');
				if (key === 'brood_debug') {
					return value === '1' || value === 'true';
				}
			}

			return false;
		} catch (error) {
			console.warn('BROOD::DEBUG:: Error checking hash for debug flag:', error);
			return false;
		}
	}

	// Media Backoff System Methods
	addToBackoff(slide, error) {
		if (!slide || !slide.slideID) return;

		const slideKey = `${slide.providerID || 'unknown'}_${slide.slideID}`;
		const now = Date.now();

		// Get existing backoff info or create new
		const existing = this.backoffSlides[slideKey];
		let failureCount = existing ? existing.failureCount + 1 : 1;

		// Exponential backoff: 1, 2, 4, 8, 16, 32, 60 minutes (max 60)
		let backoffMinutes = Math.min(Math.pow(2, failureCount - 1), 60);

		this.backoffSlides[slideKey] = {
			slideID:                slide.slideID,
			providerID:             slide.providerID,
			type:                   slide.type,
			src:                    slide.data?.src,
			description:            slide.description,
			failureCount,
			firstFailure:           existing ? existing.firstFailure : now,
			lastFailure:            now, // When this slide last failed (start of current backoff period)
			backoffDurationMinutes: backoffMinutes, // How long to wait from lastFailure
			error:                  error?.message || 'Unknown error',
		};
	}

	isInBackoff(slide) {
		if (!slide || !slide.slideID) return false;

		const slideKey = `${slide.providerID || 'unknown'}_${slide.slideID}`;
		const backoffInfo = this.backoffSlides[slideKey];

		if (!backoffInfo) return false;

		const now = Date.now();

		// Calculate how much time has passed since the slide was added to backoff
		const timeSinceBackoffStart = now - backoffInfo.lastFailure;
		const backoffDurationMs = (backoffInfo.backoffDurationMinutes || backoffInfo.backoffMinutes || 1) * 60 * 1000;
		const minutesRemaining = Math.ceil(Math.max(0, backoffDurationMs - timeSinceBackoffStart) / (60 * 1000));

		if (timeSinceBackoffStart >= backoffDurationMs) {
			// Backoff period has expired, allow retry but DON'T remove from list
			// The slide will be removed only if it succeeds, or failure count will increase if it fails again
			return false; // Don't skip - allow the slide to be tried
		}

		return true; // Skip - still in backoff period
	}

	removeFromBackoff(slide) {
		if (!slide || !slide.slideID) return;

		const slideKey = `${slide.providerID || 'unknown'}_${slide.slideID}`;
		if (this.backoffSlides[slideKey]) {
			delete this.backoffSlides[slideKey];
		}
	}

	startStuckDetectionTimer() {
		if (this.stuckDetectionTimer) return;

		this.stuckDetectionTimer = setInterval(() => {
			if (!this.playing) return;

			const currSlide = this._currSlide(this.state.slides);
			if (!currSlide) return;

			// Skip detection if we're in a legitimate single-slide loop
			const isLooping = this.state.status.slideLoopCount > 0;
			const hasSingleSlide = !this.hasNextSlide();
			if (isLooping && hasSingleSlide) return;

			if (this.currentSlideStartTime) {
				const now = Date.now();
				let expectedDuration = currSlide.options?.duration || 5000;

				// For videos, try to get actual duration
				if (currSlide.type === 'video') {
					const videoElements = document.querySelectorAll('.new-brood-slide-curr');
					for (const video of videoElements) {
						if (video.getAttribute('data-desc') === currSlide.description &&
						    video.duration && !isNaN(video.duration)) {
							expectedDuration = video.duration * 1000;
							break;
						}
					}
				}

				const rawElapsed = now - this.currentSlideStartTime;
				const actualElapsed = rawElapsed - (this.totalPausedTime || 0);
				const graceTime = 5000; // transitions and errors

				if (actualElapsed > (expectedDuration + graceTime)) {
					this.next(currSlide, true, {
						forceAdvance: true,
						reason:       'stuck_detection',
					});
				}
			}
		}, 5000);
	}

	stopStuckDetectionTimer() {
		if (this.stuckDetectionTimer) {
			clearInterval(this.stuckDetectionTimer);
			this.stuckDetectionTimer = null;
		}
	}

	componentDidMount() {
		// start the slideshow
		this.play();
		this.startStuckDetectionTimer();

		// try to wait for the first provider with order 1
		setTimeout(() => {
			this.waitingForExpectedFirstProvider = false;
			this.forceUpdate();
		}, 5000);

		document.removeEventListener('keydown', this.controls);
		document.addEventListener('keydown', this.controls);

		// Fetch visual content loop providers if this is a visual content loop
		this.fetchVisualContentLoopProviders();

		// 5 minute re-fetch of visual content loop providers
		this.visualContentLoopProvidersInterval = setInterval(() => {
			this.fetchVisualContentLoopProviders();
		}, 5 * 60 * 1000);
	}

	componentWillUnmount() {
		// stop the slideshow
		this.stop();
		this.stopStuckDetectionTimer();

		if (this.db) {
			this.db.close();
		}

		// Make sure to remove the transition lock if component unmounts
		this.isTransitioning = false;
		this.transitionStartTime = null;

		document.removeEventListener('keydown', this.controls);
		clearTimeout(this.transitionTimer);
		clearTimeout(this.safetyLockTimeout);
		clearInterval(this.debugCountdownTimer);

		// Clear any other timers that might be running
		if (this.skipCheckInterval) {
			clearInterval(this.skipCheckInterval);
		}

		if (this.visualContentLoopProvidersInterval) {
			clearInterval(this.visualContentLoopProvidersInterval);
		}
	}

	componentDidUpdate(prevProps) {
		const {
			last_update,
			condor_component_list: providerComponents,
			showPhase,
			brood_enable_transition: transitionEnabled,
		} = this.props;

		if (prevProps.showPhase !== showPhase) {
			if (showPhase) {
				this.play();
			} else {
				this.stop();
			}
		}

		// TODO: is this required from the old Pelican.js?
		if (prevProps.last_update !== last_update) {
			// debugger;
		}

		if (prevProps.brood_enable_transition !== transitionEnabled) {
			this.setState({
				transitionEnabled,
				transitionDuration: (transitionEnabled) ? 1000 : 1,
			});
		}

		const sortedProviderComponents = [...providerComponents].sort(this.providerSorter);
		const providerComponentUUIDs = sortedProviderComponents.map((providerComponent) => {
			return providerComponent.props.component_name;
		});

		const {
			condor_component_list: prevProviderComponents,
		} = prevProps;

		const sortedPrevProviderComponents = [...prevProviderComponents].sort(this.providerSorter);
		const prevProviderComponentUUIDs = sortedPrevProviderComponents.map((providerComponent) => {
			return providerComponent.props.component_name;
		});

		let providerComponentsUpdated = false;
		for (let i = 0; i < providerComponentUUIDs.length; i++) {
			if (providerComponentUUIDs[i] !== prevProviderComponentUUIDs[i]) {
				providerComponentsUpdated = true;
				break;
			}
		}

		// check if the order or number of providers is
		// different, this does not check provider settings
		if (providerComponentsUpdated) {
			debugger;

			// TODO: trigger render, but don't update providers
			// this.setState({
			// 	providers: this.makeProviders()
			// });
		}
	}

	updateProvider(...newProvider) {
		const {
			is_visual_content_loop: isVisualContentLoop,
		} = this.props;

		// Track side effects per-call via closure (not instance variable)
		// to handle React 18+ batching correctly
		let pendingSideEffects = null;

		this.setState((prevState) => {
			const {
				providers,
				slides,
				visualContentLoopMissingSlideCounts,
				visualContentLoopLoopPlayCount,
				visualContentLoopPlayedSlideCounts,
			} = prevState;

			let existingProviders = {};
			for (let i = 0; i < providers.length; i++) {
				existingProviders[providers[i].providerID] = i;
			}
			const existingProvidersCount = Object.keys(existingProviders).length;

			let newProviders = [ ...providers, ];
			const disabledProviderIDs = [];
			for (let i = 0; i < newProvider.length; i++) {
				const provider = newProvider[i];
				if (provider?.options?.maxSlides === -1) {
					// maxSlides -1 means "exclude from the deck" — a re-register
					// with it must also drop the previously-registered entry, not
					// leave the stale provider playing forever
					const disabledIndex = existingProviders[provider.providerID];
					if (disabledIndex !== undefined) {
						newProviders[disabledIndex] = null;
						disabledProviderIDs.push(provider.providerID);
					}
					continue;
				}

				const existingProviderIndex = existingProviders[provider.providerID];
				if (existingProviderIndex !== undefined) {
					newProviders[existingProviderIndex] = provider;
				} else {
					newProviders.push(provider);
				}
			}
			newProviders = newProviders.filter(Boolean).sort(this.providerSorter);

			let newMissingSlideCounts = {
				...visualContentLoopMissingSlideCounts,
			};
			if (isVisualContentLoop) {
				for (let i = 0; i < newProviders.length; i++) {
					const provider = newProviders[i];

					// Don't count backfill providers - they ARE the backfill
					if (provider.is_backfill) {
						continue;
					}

					// Use effectiveSlideCount if available (accounts for folder windowing),
					// otherwise fall back to slides.length
					const visibleSlideCount = provider.effectiveSlideCount ?? provider.slides.length;
					// Account for slides already played and removed by dynamic ad providers
					// (e.g. Nova removes ads from its array after playing via afterNext).
					// Use max(visible, played) — not the sum — because:
					// - Before ads play: visible count reflects available slots
					// - After ads play and are removed: played count reflects fulfilled slots
					// - If new ads arrive mid-loop after the provider cycle completed, they
					//   won't play this loop, so we shouldn't count visible + played together
					const playedCounts = visualContentLoopPlayedSlideCounts[visualContentLoopLoopPlayCount] || {};
					const playedCount = playedCounts[provider.providerID] || 0;
					const fulfilledSlots = Math.max(visibleSlideCount, playedCount);

					if (fulfilledSlots < provider.slots) {
						const missingCount = (provider.slots - fulfilledSlots);

						if (typeof newMissingSlideCounts[visualContentLoopLoopPlayCount] === 'undefined') {
							newMissingSlideCounts[visualContentLoopLoopPlayCount] = {};
						}

						newMissingSlideCounts[visualContentLoopLoopPlayCount][provider.providerID] = missingCount;
					} else {
						// Provider has enough slides to fill its slots
						if (typeof newMissingSlideCounts[visualContentLoopLoopPlayCount] === 'undefined') {
							newMissingSlideCounts[visualContentLoopLoopPlayCount] = {};
						}
						newMissingSlideCounts[visualContentLoopLoopPlayCount][provider.providerID] = 0;
					}
				}
			}

			// a disabled provider's loop deficit and play tracking must go with
			// it: skipper sums the deficits into backfill demand, and a later
			// re-enable would otherwise inherit exhausted plays/attempts
			const disabledProviderState = {};
			if (disabledProviderIDs.length > 0) {
				const withoutDisabled = (map = {}) => {
					const next = { ...map, };
					for (const providerID of disabledProviderIDs) {
						delete next[providerID];
					}
					return next;
				};

				if (newMissingSlideCounts[visualContentLoopLoopPlayCount]) {
					newMissingSlideCounts[visualContentLoopLoopPlayCount] = withoutDisabled(newMissingSlideCounts[visualContentLoopLoopPlayCount]);
				}
				disabledProviderState.providerPlays = withoutDisabled(prevState.providerPlays);
				disabledProviderState.providerAttempts = withoutDisabled(prevState.providerAttempts);
				disabledProviderState.providerSlideIndex = withoutDisabled(prevState.providerSlideIndex);
			}

			let newSlides = this.makeSlides(newProviders);
			if (newSlides.length === 0) {
				return {
					providers:   newProviders,
					slides:      newSlides,
					currSlideID: false,
					nextSlideID: false,
					visualContentLoopMissingSlideCounts: newMissingSlideCounts,
					...disabledProviderState,
				};
			}

			let currSlide = this._currSlide(slides);
			if (!currSlide) {
				if (newSlides.length > 0) {
					currSlide = newSlides[0];

					let i = 0;
					// resolve skippers against the incoming provider list — a
					// provider registering for the first time isn't in committed
					// state yet, so this.skipper alone would never consult it
					while (this.skipper(currSlide, newProviders)) {
						currSlide = this._nextSlide(newSlides, currSlide, newProviders);

						// every slide skipped: keep the first one current so
						// BroodSlide's skip poll retries it (e.g. once a schedule
						// window opens); with no current slide nothing polls and
						// playback stalls until the provider re-registers
						if (i > newSlides.length) {
							currSlide = newSlides[0];
							break;
						}

						i++;
					}
				} else {
					return {
						providers:   newProviders,
						slides:      newSlides,
						currSlideID: false,
						nextSlideID: false,
						...disabledProviderState,
					};
				}
			}

			const {
				providerID,
				slideID,
			} = currSlide;

			let hasCurrSlideInNewSlides = newSlides.find((slide) => {
				return (slide.providerID === providerID && slide.slideID === slideID);
			});

			if (!hasCurrSlideInNewSlides) {
				let providerIndex = 0;
				for (let i = 0; i < newSlides.length; i++) {
					if (newSlides[i].providerID === providerID) {
						providerIndex = i;
						break;
					}
				}

				currSlide = newSlides[providerIndex];
			}

			let hasProviderUpdates = false;
			if (providers.length !== newProviders.length) {
				hasProviderUpdates = true;
			}

			// Track side effects to run in callback (not in updater)
			let shouldCallIsNotOnlyProvider = false;
			let isNotOnlyProviderRef = null;

			if (hasProviderUpdates) {
				if (existingProvidersCount > 0 && newProviders.length > existingProvidersCount) {
					const provider = currSlide.provider;

					if (provider && provider.isNotOnlyProvider && typeof provider.isNotOnlyProvider === 'function') {
						shouldCallIsNotOnlyProvider = true;
						isNotOnlyProviderRef = provider;
					}
				}
			}

			if (!hasProviderUpdates) {
				for (let i = 0; i < providers.length; i++) {
					if (providers[i].providerID !== newProviders[i].providerID) {
						hasProviderUpdates = true;
						break;
					}
				}
			}

			let hasSlideUpdates = false;
			if (slides.length !== newSlides.length) {
				hasSlideUpdates = true;
			}

			if (!hasSlideUpdates) {
				for (let i = 0; i < slides.length; i++) {
					if (slides[i].slideID !== newSlides[i].slideID) {
						hasSlideUpdates = true;
						break;
					}
				}
			}

			if (!hasProviderUpdates) {
				console.log('no provider updates');
			}

			if (!hasSlideUpdates) {
				console.log('no slide updates');
			}

			let currSlideID = currSlide.slideID;
			let nextSlideID = false;

			// Track if we should clear waitingForExpectedFirstProvider in callback
			let shouldClearWaitingForExpectedFirstProvider = false;

			if (this.waitingForExpectedFirstProvider) {
				const expectedFirstProvider = newProviders.find((provider) => {
					return !provider.is_backfill && provider.order === 1;
				});
				if (expectedFirstProvider) {
					if (expectedFirstProvider.providerID !== currSlide.providerID) {
						let expectedFirstSlide;
						for (let i = 0; i < expectedFirstProvider.slides.length; i++) {
							const slide = expectedFirstProvider.slides[i];
							// same override as the fresh-registration loop above:
							// the expected first provider may not be in committed
							// state yet
							if (!this.skipper(slide, newProviders)) {
								expectedFirstSlide = slide;
								break;
							}
						}

						if (expectedFirstSlide) {
							currSlideID = expectedFirstSlide.slideID;
							currSlide = expectedFirstSlide;
						}
					}

					shouldClearWaitingForExpectedFirstProvider = true;
				}
			}

			// peek: currSlide is playing, so ask providers what follows it AFTER
			// it completes (Pelican folder cursor is still parked on currSlide
			// until then — without the peek a folder successor is never
			// pre-mounted and can't crossfade in). (DEV-12201)
			const nextState = {
				providers: newProviders,
				slides:    newSlides,
				currSlideID,
				nextSlideID,
				prevSlide: currSlide,
				visualContentLoopMissingSlideCounts: newMissingSlideCounts,
				...disabledProviderState,
			};

			if (this.isOnlyProviderWithSlides(currSlide.providerID, newSlides)) {
				const nextProviderPlays = {};
				const nextProviderAttempts = {};
				for (const [ providerID, slideIDSet, ] of Object.entries(nextState.providerPlays ?? prevState.providerPlays)) {
					nextProviderPlays[providerID] = new Set(slideIDSet);
				}
				for (const [ providerID, slideIDSet, ] of Object.entries(nextState.providerAttempts ?? prevState.providerAttempts)) {
					nextProviderAttempts[providerID] = new Set(slideIDSet);
				}
				nextProviderPlays[currSlide.providerID] = new Set();
				nextProviderAttempts[currSlide.providerID] = new Set();
				nextState.providerPlays = nextProviderPlays;
				nextState.providerAttempts = nextProviderAttempts;
			}

			// Predict what follows currSlide with the attempts that will be in
			// effect after this update (the sole-provider reset above), so the
			// pre-mounted `next` matches what next() will actually pick. (DEV-12201)
			const attemptsAfterUpdate = nextState.providerAttempts ?? prevState.providerAttempts;
			const slideIndexAfterUpdate = nextState.providerSlideIndex ?? prevState.providerSlideIndex;
			const nextSlide = this._predictSlideAfter(currSlide, attemptsAfterUpdate, newProviders, slideIndexAfterUpdate)
				|| this._nextSlide(newSlides, currSlide, newProviders);
			if (nextSlide) {
				nextState.nextSlideID = nextSlide.slideID;
			}

			// Store pending side effects in closure variable
			pendingSideEffects = {
				shouldCallIsNotOnlyProvider,
				isNotOnlyProviderRef,
				shouldClearWaitingForExpectedFirstProvider,
			};

			return nextState;
		}, () => {
			this.updated = true;

			// Execute deferred side effects from closure
			if (pendingSideEffects) {
				if (pendingSideEffects.shouldCallIsNotOnlyProvider && pendingSideEffects.isNotOnlyProviderRef) {
					pendingSideEffects.isNotOnlyProviderRef.isNotOnlyProvider();
				}
				if (pendingSideEffects.shouldClearWaitingForExpectedFirstProvider) {
					this.waitingForExpectedFirstProvider = false;
					// The flag is an instance variable, not state — mutating it
					// here doesn't queue a re-render, and renderSlides() returned
					// null in the render that just committed (because the flag
					// was still true at commit time). For a single-provider deck
					// (Pelican-only VCL), nothing else would trigger a re-render
					// until the 5s setTimeout fallback, leaving the screen blank.
					// forceUpdate so the next render picks up the cleared flag
					// and emits the slides immediately.
					this.forceUpdate();
				}
			}

			console.log('BROOD::updateProvider:', newProvider, this.state.currSlideID);
		});
	}

	// Intentionally a no-op. A failed VCL provider refetch empties
	// visualContentLoopProviders, which unmounts every provider component;
	// acting on unmount would drop that provider's content until the next
	// good fetch. VCL removals go through visualContentLoopProvidersRemoved,
	// and disabling goes through updateProvider with maxSlides -1.
	removeProvider(providerID) {
	}

	makeSlides(providers = []) {
		const {
			sort: broodSortType,
			is_visual_content_loop: isVisualContentLoop,
		} = this.props;

		const slides = [];

		for (const provider of providers) {
			const {
				options: providerOptions,
			} = provider;

			const {
				// providers emit the sort preference as options.sortType
				// (Pelican makeProvider et al.)
				sortType: providerSortType,
			} = providerOptions;

			if (!provider.skipper || typeof provider.skipper !== 'function') {
				// eslint-disable-next-line no-unused-vars
				provider.skipper = (slide) => {
					return false;
				};
			}

			if (providerSortType === 'random') {
				provider.slides.sort(() => {
					return Math.random() - 0.5;
				});
			}

			for (let i = 0; i < provider.slides.length; i++) {
				const slide = provider.slides[i];

				slide.providerID = provider.providerID;
				if (isVisualContentLoop) {
					if (!slide.provider) {
						slide.provider = provider || {};
					}
					slide.provider.providerID = provider.providerID;

					slide.isBackfill = provider.is_backfill || false;
				}

				slides.push(slide);
			}
		}

		const providerCount = Object.keys(providers).length;
		if (providerCount > 1 && broodSortType === 'random') {
			slides.sort(() => {
				return Math.random() - 0.5;
			});
		}

		return slides;
	}

	controls = (e) => {
		switch (e.key) {
		case 'p':
			if (this.playing) {
				this.stop();
			} else {
				this.play();
			}

			break;

		case 'n':
			this.next(true, true, {});

			break;

		case 't':
			this.setState({
				transitionEnabled:  !this.state.transitionEnabled,
				transitionDuration: (!this.state.transitionEnabled) ? 1000 : 1,
			});

			break;

		case 'D': // Shift+D
			if (e.shiftKey) {
				this.showDebugOverlay = !this.showDebugOverlay;
				this.forceUpdate();
			}
			break;

		default:
			break;
		}
	}

	play = () => {
		if (this.playing) {
			return;
		}
		// console.log('play');

		const wasPaused = this.pausedAt !== null;

		if (wasPaused && this.pausedAt) {
			// We're resuming from a pause - add the paused duration to total paused time
			this.totalPausedTime += Date.now() - this.pausedAt;
			this.pausedAt = null;
		}

		this.playing = true;
		this.setState({
			status: {
				...this.state.status,
				playing: true,
			},
			visualContentLoopLoopPlayCount: this.state.visualContentLoopLoopPlayCount || 0,
		});
	}

	stop = () => {
		if (!this.playing) {
			return;
		}

		// Mark when we paused
		this.pausedAt = Date.now();

		this.playing = false;
		this.isTransitioning = false; // Ensure lock is released on stop
		// DON'T reset slide timing here - we need it for resume
		// this.currentSlideStartTime = null; // Reset slide timing
		// this.debugElapsed = 0;
		// this.debugCountdown = 0;
		// this.debugPausedTime = 0;

		this.setState({
			status: {
				...this.state.status,
				playing: false,
			},
		});
	}

	_index = (slides = [], currSlideIndex = 0, diff = 0) => {
		if (slides.length <= 1) {
			return 0;
		}

		// modular wraparound — a diff spanning multiple loops must still land
		// in range, not just subtract the length once
		return (currSlideIndex + diff) % slides.length;
	}

	_currSlideProvider = (slides = []) => {
		const currSlide = this._currSlide(slides);
		if (!currSlide) {
			return false;
		}

		const {
			providers,
		} = this.state;

		return providers.find((provider) => {
			return provider.providerID === currSlide.providerID;
		});
	}

	_currSlide = (slides = [], slideID = false) => {
		const {
			currSlideID,
		} = this.state;

		if (slides.length === 0 || !currSlideID) {
			return null;
		}

		return slides.find((slide) => {
			if (slideID) {
				return slide.slideID === slideID;
			}

			return slide.slideID === currSlideID;
		});
	}

	// peek (optional) is forwarded to provider skippers — see skipper().
	_nextSlide = (slides = [], currSlide, providersOverride, peek) => {
		if (!currSlide || !currSlide.slideID) {
			// try to find it at a new index in the current slides
			currSlide = this._currSlide(slides);

			// if we still can't find it, then slides have been
			// updated to no longer include our current slide
			// we'll need to get it from state.prevSlide instead
			if (!currSlide) {
				const {
					prevSlide,
				} = this.state;

				currSlide = prevSlide;

				// if we still can't find it, then we're in a bad state somehow
				// and we should just return the first slide in the new slides
				// this could cause a slide to get skipped, but it's better than
				// crashing the slideshow
				if (!currSlide) {
					if (slides.length > 0) {
						currSlide = slides[0];
					}
				}
			}
		}

		if (slides.length === 0) {
			return false;
		}

		if (slides.length === 1) {
			return slides[0];
		}

		let currSlideIndex = 0;
		for (let i = 0; i < slides.length; i++) {
			if (slides[i].slideID === currSlide.slideID) {
				currSlideIndex = i;
				break;
			}
		}

		let nextSlideIndex = this._index(slides, currSlideIndex, 1);
		let nextSlide = slides[nextSlideIndex];

		let nextCount = 0;
		let slideCount = slides.length;

		while (currSlide.slideID === nextSlide.slideID || this.skipper(nextSlide, providersOverride, peek)) {
			nextSlide = slides[this._index(slides, currSlideIndex, nextCount)];
			nextCount++;

			if (slideCount === 0) {
				return currSlide;
			}
			slideCount--;
		}

		return nextSlide;
	}

	// beforeNext, beforeLastSlide,
	_beforeNext(currSlide, isLastSlide = false) {
		const {
			is_visual_content_loop: isVisualContentLoop,
		} = this.props;

		const {
			providers,
			slides,
		} = this.state;

		if (isVisualContentLoop) {
			const {
				visualContentLoopLoopPlayCount,
				visualContentLoopMissingSlideCounts,
			} = this.state;

			const currSlideIndex = slides.findIndex((slide) => {
				return (slide.providerID === currSlide.providerID && slide.slideID === currSlide.slideID);
			});
			
			const nextSlide = this._nextSlide(slides, currSlide);
			const nextSlideIndex = slides.findIndex((slide) => {
				return (slide.providerID === nextSlide.providerID && slide.slideID === nextSlide.slideID);
			});

			if (currSlideIndex > nextSlideIndex) {
				// Recalculate missing counts fresh for the new loop using current
				// provider state and reset played/error counts (all 0 at loop start).
				const freshMissingCounts = {};
				for (const provider of providers) {
					if (provider.is_backfill) {
						continue;
					}

					const visibleSlideCount = provider.effectiveSlideCount ?? provider.slides.length;
					if (visibleSlideCount < provider.slots) {
						freshMissingCounts[provider.providerID] = (provider.slots - visibleSlideCount);
					} else {
						freshMissingCounts[provider.providerID] = 0;
					}
				}

				this.setState({
					visualContentLoopLoopPlayCount: (visualContentLoopLoopPlayCount + 1),
					visualContentLoopMissingSlideCounts: {
						[visualContentLoopLoopPlayCount + 1]: freshMissingCounts,
					},
					// new loop, reset error counts
					visualContentLoopErrorSlideCounts: {
						[visualContentLoopLoopPlayCount + 1]: 0,
					},
					// new loop, reset played counts
					visualContentLoopPlayedSlideCounts: {
						[visualContentLoopLoopPlayCount + 1]: {},
					},
				});
			}
		}

		if (currSlide && currSlide.provider && typeof currSlide.provider.beforeNext === 'function') {
			if (this.debug) {
				console.log('BROOD::DEBUG::beforeNext: curr:', currSlide.providerID, currSlide.slideID);
			}

			currSlide.provider.beforeNext(currSlide);
		}

		if (isLastSlide) {
			const eventIDs = [];
			for (const provider of providers) {
				if (provider.eventID) {
					if (eventIDs.includes(provider.eventID)) {
						continue;
					}

					eventIDs.push(provider.eventID);
				}

				if (provider.beforeLastSlide && typeof provider.beforeLastSlide === 'function') {
					provider.beforeLastSlide(slides);
				}
			}
		}
	}

	// afterLastSlide, afterNext, beforeLastSlide
	_afterNext(currSlide, isLastSlide = false) {
		if (this.debug) {
			console.log('BROOD::DEBUG::afterNext');
		}

		const {
			providers,
			slides,
		} = this.state;

		// prevSlide just finished, currSlide is now playing
		if (isLastSlide) {
			this.sendPoPQueue();
			const eventIDs = [];
			for (const provider of providers) {
				if (provider.eventID) {
					if (eventIDs.includes(provider.eventID)) {
						continue;
					}

					eventIDs.push(provider.eventID);
				}
			}

			if (currSlide && currSlide.provider && typeof currSlide.provider.afterLastSlide === 'function') {
				const prevSlide = currSlide;
				currSlide.provider.afterLastSlide(prevSlide);
			}
		}

		if (currSlide && currSlide.provider && typeof currSlide.provider.afterNext === 'function') {
			const {
				// eslint-disable-next-line no-shadow
				slides,
			} = this.state;

			const prevSlide = currSlide;
			const nextCurrSlide = this._currSlide(slides);

			if (this.debug) {
				console.log('BROOD::DEBUG::afterNext: prev:', prevSlide.providerID, prevSlide.slideID);
				console.log('BROOD::DEBUG::afterNext: curr:', nextCurrSlide.providerID, nextCurrSlide.slideID);
			}

			// afterNext(prevSlides=slides, prevSlide, currSlide)
			currSlide.provider.afterNext(slides, prevSlide, nextCurrSlide);
		}
	}

	// hooks: beforeNext, beforeLastSlide, afterLastSlide, afterNext
	next = (slide = false, curr = false, slidePlayInfo = {}) => {
		if (!this.playing) {
			console.log('BROOD::NEXT_BLOCKED - Not playing');
			return;
		}

		if (!curr && slide) {
			console.log('BROOD::NEXT_NOT_CURR:', slide?.description || slide.slideID);
			return;
		}

		const {
			is_visual_content_loop: isVisualContentLoop,
		} = this.props;

		const {
			slides, providers, status: {
				slideLoopCount,
			}, transitionDuration,
		} = this.state;
		const currSlide = this._currSlide(slides);

		// Nothing to advance from: the deck is empty (updateProvider publishes
		// currSlideID=false with an empty slide list). A single-provider Nova
		// program with limit 1 hits this on every play — afterNext removes the
		// spent ad and republishes an empty buffer before the refill lands, and
		// BroodSlide's instant-skip path (the spent slide now fails Nova's
		// skipper) dispatches next(slide, true, {skipped}) into that window.
		// updateProvider re-seeds currSlideID when slides come back, so there
		// is nothing for next() to do here. Returning (rather than falling
		// through) is required: the isOnlySlide branch and the post-setState
		// backoff bookkeeping below dereference currSlide. (DEV-12268)
		if (!currSlide) {
			console.log('BROOD::NEXT_BLOCKED - No current slide');
			return;
		}

		const currProvider = providers.find((p) => {
			return p.providerID === currSlide.providerID;
		});

		// If the slide just ended in an error, register the backoff entry NOW —
		// before beforeNextPick or the picker run — so anything that asks
		// "is currSlide playable?" via this.skipper / broodSkipper sees the
		// updated state. Previously addToBackoff happened in the setState
		// callback below, which fires after the picker has already run; with
		// no other playable slide available (e.g. a back-to-back error on the
		// next non-folder slide too), the picker's second-pass fallback would
		// re-pick the just-errored slide and we'd display it for a full
		// slide_duration before recovering.
		//
		// Structural single-slide exception: when the deck is one slide there
		// is nowhere to advance to, so backoff is purely destructive — it
		// flips skipper(currSlide) to true, BroodSlide renders null (line
		// ~2715), and React unmounts the <video> element. That unmount aborts
		// any in-flight video.play() promise with "AbortError: The play()
		// request was interrupted because the media was removed from the
		// document", which is exactly what kills the webm-404 → mp4-fallback
		// path on single-video broods: the watchdog escalation happens while
		// the browser is still buffering the mp4 <source>, and the resulting
		// unmount kills the mp4 attempt before it can start. Skipping backoff
		// here lets the <video>'s native resource selection finish.
		if (slidePlayInfo.error && currSlide && slides.length > 1) {
			this.addToBackoff(currSlide, slidePlayInfo.error);
		}

		// Provider hook: synchronously update rotation state BEFORE the picker
		// runs. Providers that paginate (Pelican folder rotation) use this to
		// advance their cursor in time for the picker to see the new state on
		// its next skipper call. Must be side-effect-free for providers that
		// don't implement it. Runs even when slidePlayInfo.error or skipped —
		// the slide ended, regardless of cause.
		//
		// Args:
		//   currSlide      — the slide that just finished
		//   broodSkipper   — bound Brood.skipper so the provider can ask
		//                    "is this OTHER slide actually playable?"
		//                    (backoff-aware; required for cursor advance past
		//                    broken slides and substitution semantics)
		//   slidePlayInfo  — { error, skipped, ... }; lets the provider treat
		//                    error vs success differently (e.g. errors advance
		//                    cursor but don't count toward folder_slots quota)
		//
		// Return value (optional): { loopWrapped: true } — the provider just
		// completed a full rotation round (Pelican's pure-folder quota wrap).
		// Folded into the cycle check below so the provider yields AT the
		// round boundary: the attempts set is per-visit, not per-round, so
		// without this the wrap-reopened cursor slide would play immediately
		// and every steady-state round phase-shifts by one slide. (DEV-11981)
		let providerLoopWrapped = false;
		if (currSlide && currProvider && typeof currProvider.beforeNextPick === 'function') {
			// this.skipper is already an arrow-property — no .bind() needed.
			const hookResult = currProvider.beforeNextPick(currSlide, this.skipper, slidePlayInfo);
			providerLoopWrapped = !!(hookResult && hookResult.loopWrapped);
		}

		// --- State Calculation ---
		// Create temporary state objects to calculate the next state without relying on this.state mid-function
		let nextProviderPlays = {};
		let nextProviderAttempts = {};
		// Deep copy Sets for each provider
		for (const [ providerID, slideIDSet, ] of Object.entries(this.state.providerPlays)) {
			nextProviderPlays[providerID] = new Set(slideIDSet);
		}
		for (const [ providerID, slideIDSet, ] of Object.entries(this.state.providerAttempts)) {
			nextProviderAttempts[providerID] = new Set(slideIDSet);
		}
		let nextProviderSlideIndex = {
			...this.state.providerSlideIndex,
		};

		// Add slideID to appropriate sets based on the outcome
		if (currProvider && currSlide) {
			// Always track attempts (prevents retries of problematic slides)
			if (!nextProviderAttempts[currProvider.providerID]) {
				nextProviderAttempts[currProvider.providerID] = new Set();
			}
			nextProviderAttempts[currProvider.providerID].add(currSlide.slideID);

			// Only track successes for limit checking
			if (!slidePlayInfo.error && !slidePlayInfo.skipped) {
				if (!nextProviderPlays[currProvider.providerID]) {
					nextProviderPlays[currProvider.providerID] = new Set();
				}
				nextProviderPlays[currProvider.providerID].add(currSlide.slideID);
			}
		}

		// VCL played count is tracked for the NEXT slide (about to start playing),
		// not the current slide (just finished). Calculated after nextSlide is determined.
		let nextPlayedSlideCounts = null;

		let isLastSlide = false;
		let nextSlide = null;

		// state won't be updated by the time we need this info.
		//
		// Defensive: updateProvider always rebuilds slides from providers, so a
		// current slide with no matching provider has no known producer today.
		// If one appears, fall through with an empty attempts set; the
		// round-robin probe below still advances the deck. (DEV-12268)
		const attemptedSlides = (currProvider && nextProviderAttempts[currProvider.providerID]) || new Set();

		// --- Provider Limit Check & Cycling Logic ---
		if (currProvider) {
			const limit = currProvider.options?.maxSlides;
			const successfulSlides = nextProviderPlays[currProvider.providerID] || new Set();
			const plays = successfulSlides.size; // Use Set size to count unique successful slides played
			const attempts = attemptedSlides.size; // Use Set size to count unique slides attempted
			const playableProviderSlides = currProvider.slides.filter((s) => {
				return !this.skipper(s);
			});
			const dynamicPlayableSlides = playableProviderSlides.length;
			const hasFolder = currProvider.slides.some((s) => s.folder_id);
			const totalProviderPlayableSlides = (hasFolder && currProvider.effectiveSlideCount > 0)
				? Math.min(currProvider.slides.length, Math.max(dynamicPlayableSlides, currProvider.effectiveSlideCount))
				: dynamicPlayableSlides;
			const limitReached = limit && plays >= limit;
			// Cycle-complete means "no playable slide remains that we haven't
			// attempted this visit". The former count comparison
			// (plays/attempts >= currently-playable count) double-stepped on
			// folder decks: a played folder slide leaves the playable set when
			// its folder hits quota (beforeNextPick runs before this check), so
			// the count check fired mid-round and the provider yielded with
			// unplayed slides still owed — backfill played in the middle of the
			// deck's round instead of after it. For a static playable set the
			// membership test is equivalent to the count comparison (attempts
			// only ever holds this provider's slides and resets each visit), so
			// folder-free decks and dynamic providers are unaffected.
			// (DEV-11981)
			const hasUnattemptedPlayableSlide = playableProviderSlides.some((s) => {
				return !attemptedSlides.has(s.slideID);
			});

			// Provider cycle is complete if we hit the limit, nothing playable is
			// left unattempted, or the provider reported a completed rotation
			// round (folder-quota wrap)
			const providerCycleComplete = (limitReached || !hasUnattemptedPlayableSlide || providerLoopWrapped);
			isLastSlide = providerCycleComplete;
			if (this.isOnlyProviderWithSlides(currProvider.providerID)) {
				const totalSlideshowPlayableSlides = slides.length;
				if (plays <= totalSlideshowPlayableSlides) {
					isLastSlide = false;
				}
			}

			if (slidePlayInfo.error || slidePlayInfo.skipped) {
				isLastSlide = false; // Don't treat skipped slides as last slides
			}

			// Deliberately gated on providerCycleComplete, NOT isLastSlide: the
			// error/skip suppression above only exists to keep last-slide side
			// effects (PoP queue flush, before/afterLastSlide hooks) from firing
			// for plays that didn't complete. Switching away must still be
			// allowed on an error/skip — when a dead provider has nothing
			// unattempted-playable left, staying means a stuck screen until
			// backoff expires, and the fallback path below would switch anyway
			// but without saving this provider's resume position. Same gating
			// the pre-DEV-11981 count check had (allSlidesAttempted counted
			// errored attempts).
			if (providerCycleComplete && !this.isOnlyProviderWithSlides(currProvider.providerID)) {
				console.log(`BROOD::DEBUG:: Provider cycle complete for ${currProvider.providerID}. Switching...`);
				console.log(`BROOD::DEBUG:: Reason - limitReached: ${limitReached}, hasUnattemptedPlayableSlide: ${hasUnattemptedPlayableSlide}, providerLoopWrapped: ${providerLoopWrapped}`);
				console.log(`BROOD::DEBUG:: Current slide: ${currSlide.description || currSlide.slideID} (index: ${currProvider.slides.findIndex((s) => {
					return s.slideID === currSlide.slideID;
				})})`);
				console.log(`BROOD::DEBUG:: Stats - successful: ${plays}/${limit || 'N/A'}, attempted: ${attempts}/${totalProviderPlayableSlides}`);

				nextProviderPlays[currProvider.providerID] = new Set(); // Reset successful plays for the provider we're leaving
				nextProviderAttempts[currProvider.providerID] = new Set(); // Reset attempts for the provider we're leaving

				// Smart restart strategy:
				// - If we have more slides than the limit, continue from where we left off for variety
				// - If limit >= total slides, restart from beginning for clean cycling
				let resumeFromIndex;
				if (limit && limit < totalProviderPlayableSlides) {
					// More slides than limit - continue from current position for variety
					resumeFromIndex = currProvider.slides.findIndex((s) => {
						return s.slideID === currSlide.slideID;
					});
					console.log(`BROOD::DEBUG:: ${currProvider.providerID} has more slides than limit (${totalProviderPlayableSlides} > ${limit}), continuing from current position`);
				} else {
					// Limit >= total slides - restart from beginning for clean cycling
					resumeFromIndex = -1;
					console.log(`BROOD::DEBUG:: ${currProvider.providerID} limit >= total slides (${limit} >= ${totalProviderPlayableSlides}), restarting from beginning`);
				}

				// Since we just reset the attempts, we need to find slides without checking the old state
				// Simple inline slide finder that ignores attempt history since we just reset
				let nextResumeSlide = null;
				const slides = currProvider.slides;
				for (let i = 1; i <= slides.length; i++) {
					const index = (resumeFromIndex + i) % slides.length;
					const slide = slides[index];
					if (!this.skipper(slide)) {
						nextResumeSlide = {
							slide,
							index,
						};
						break;
					}
				}

				if (nextResumeSlide) {
					nextProviderSlideIndex[currProvider.providerID] = nextResumeSlide.index;
					console.log(`BROOD::DEBUG:: ${currProvider.providerID} will resume from slide index ${nextResumeSlide.index} (${nextResumeSlide.slide.description || nextResumeSlide.slide.slideID})`);
				} else {
					console.log(`BROOD::DEBUG:: ${currProvider.providerID} - no next resume slide found`);
				}

				const nextProvider = this._getNextProviderWithSlides(currProvider.providerID);
				if (nextProvider) {
					console.log(`Switching to next provider: ${nextProvider.providerID}`);
					const startIndex = nextProviderSlideIndex[nextProvider.providerID] || -1;
					console.log(`BROOD::DEBUG:: Next provider start index: ${startIndex}`);
					// FIX: When resuming from a saved position, use startIndex - 1
					// so _findNextPlayableSlide finds the slide at the saved index
					const adjustedStartIndex = (startIndex >= 0) ? startIndex - 1 : startIndex;
					const next = this._findNextPlayableSlide(nextProvider, adjustedStartIndex, attemptedSlides);
					if (next) {
						nextSlide = next.slide;
						nextProviderSlideIndex[nextProvider.providerID] = next.index;
						console.log(`BROOD::DEBUG:: Next provider will play slide: ${next.slide.description || next.slide.slideID} (index: ${next.index})`);
					}
				}
			}
		}

		// If no new slide from a provider switch, find next slide in current provider
		if (!nextSlide && currProvider) {
			const startIndex = currProvider.slides.findIndex((s) => {
				return s.slideID === currSlide.slideID;
			});
			const next = this._findNextPlayableSlide(currProvider, startIndex, attemptedSlides);
			if (next) {
				nextSlide = next.slide;
				nextProviderSlideIndex[currProvider.providerID] = next.index;
			}
		}

		// Fallback for safety: if still no next slide, loop from the beginning of available providers
		if (!nextSlide) {
			const nextProvider = this._getNextProviderWithSlides((currProvider || {})?.providerID);
			if (nextProvider) {
				const next = this._findNextPlayableSlide(nextProvider, -1, attemptedSlides);
				if (next) {
					nextSlide = next.slide;
					nextProviderSlideIndex[nextProvider.providerID] = next.index;
					if (currProvider) {
						nextProviderPlays[currProvider.providerID] = new Set(); // Reset old provider successes
						nextProviderAttempts[currProvider.providerID] = new Set(); // Reset old provider attempts
					}
				}
			}
		}

		// Round-robin probe when EVERY slide across EVERY provider is currently
		// unplayable (all in backoff, or all rejected by provider.skipper). The
		// playable-slide picker above came back empty, but the deck still has
		// more than one slide — so this isn't a structural single-slide loop,
		// it's a transient all-blocked state.
		//
		// Without this, isOnlySlide=true would fire below, currSlideID would
		// stay pinned to one slide, and BroodSlide would render null because
		// skipper says skip. The user sees a grey screen until backoff windows
		// expire (1-60 min depending on failureCount). The earlier "1 bad
		// slide" testing only covered single-provider / single-bad-slide
		// scenarios; the multi-provider-all-blocked failure mode is what
		// surfaced on local dev when no S3 fetches resolved.
		//
		// Strategy: pick the next slide by global index regardless of skipper.
		// It'll still be rejected by BroodSlide's render-time skip check, but
		// state.currSlideID advancing causes:
		//   1. Pelican.beforeNextPick to run with the new current → folder
		//      cursor advances past blocked slides.
		//   2. BroodSlide useEffect deps change (slide ref changes) → the
		//      curr/skip path re-fires its setInterval(next, 5000) freshly.
		//   3. Each step re-evaluates skipper, catching any slide whose
		//      backoff window has expired since the last attempt.
		// Net: the system visibly cycles through the deck instead of stalling.
		if (!nextSlide && currSlide && slides.length > 1) {
			const currIdx = slides.findIndex((s) => {
				return s.slideID === currSlide.slideID;
			});
			const probeIdx = (currIdx >= 0) ? (currIdx + 1) % slides.length : 0;
			nextSlide = slides[probeIdx];
		}

		// --- Debug Logging ---
		if (currSlide && currProvider) {
			const slideIndexInProvider = currProvider.slides.findIndex((s) => {
				return s.slideID === currSlide.slideID;
			});
			const limit = currProvider.options?.maxSlides || 'N/A';
			const totalSlides = currProvider.slides.length;
			const successfulSlides = nextProviderPlays[currProvider.providerID] || new Set();
			const attemptedSlides = nextProviderAttempts[currProvider.providerID] || new Set();
			console.log(
				`BROOD::DEBUG:: Status --- \n` +
				`  Slide: ${currSlide?.description || currSlide?.slideID}\n` +
				`  Provider: ${currProvider.providerID}\n` +
				`  Index in Provider: ${slideIndexInProvider} (Total slides: ${totalSlides})\n` +
				`  Provider Limit: ${limit}\n` +
				`  Successful slides: ${successfulSlides.size} (${Array.from(successfulSlides).join(', ')})\n` +
				`  Attempted slides: ${attemptedSlides.size} (${Array.from(attemptedSlides).join(', ')})`
			);
		}

		// Block next call if a transition is already in progress, but add safety mechanism
		if (this.isTransitioning && !slidePlayInfo.error && !slidePlayInfo.forceAdvance) {
			console.log('BROOD::NEXT_BLOCKED - Transition in progress');

			// Safety mechanism: if we've been stuck in transition for too long, force reset
			if (!this.transitionStartTime) {
				this.transitionStartTime = Date.now();
			} else if (Date.now() - this.transitionStartTime > (transitionDuration * 3)) {
				console.warn('BROOD::FORCE_UNLOCK - Transition stuck, forcing unlock');
				this.isTransitioning = false;
				this.transitionStartTime = null;
				clearTimeout(this.safetyLockTimeout);
				clearTimeout(this.transitionTimer);
				// Continue with the function execution
			} else {
				return;
			}
		}

		// Set transition flag at the start
		this.isTransitioning = true;
		this.transitionStartTime = Date.now();

		// Enhanced safety timeout to release lock if something goes wrong
		clearTimeout(this.safetyLockTimeout);
		this.safetyLockTimeout = setTimeout(() => {
			console.warn('BROOD::SAFETY_RELEASE - Transition lock automatically released (timeout)');
			this.isTransitioning = false;
			this.transitionStartTime = null;
		}, Math.max(transitionDuration * 2, 2000));

		// Structural single-slide check. Previously `!this.hasNextSlide()`,
		// which also returned true when *every* slide in a multi-slide deck
		// was currently unplayable — that conflated "this kiosk shows one
		// image forever" with "transient all-blocked", and the latter case
		// got stuck on a pinned currSlideID showing nothing. Now we only
		// enter this branch when the deck is structurally one slide; the
		// transient all-blocked case is handled by the round-robin probe
		// above (which sets nextSlide so the regular path advances).
		const isOnlySlide = (slides.length <= 1);
		if (isOnlySlide) {
			this._beforeNext(currSlide, true);

			this.setState({
				status: {
					...this.state.status,
					slideLoopCount: (slideLoopCount + 1),
				},
			}, () => {
				if (!slidePlayInfo.error && !slidePlayInfo.skipped) {
					this._afterNext(currSlide, true);
				}

				this.isTransitioning = false; // Release lock when done
				this.transitionStartTime = null;
				clearTimeout(this.safetyLockTimeout);
			});

			return;
		}

		this._beforeNext(currSlide, isLastSlide);

		// Pre-increment VCL played count for the NEXT slide (about to start playing).
		// This ensures any updateProvider that fires during the slide's play time sees
		// the correct count. With R19 batching, tracking at finish time (_afterNext) is
		// too late — the skipper evaluates before the state commits.
		if (isVisualContentLoop && nextSlide && !nextSlide.isBackfill) {
			const nextSlideProvider = providers.find((p) => p.providerID === nextSlide.providerID);
			if (nextSlideProvider && nextSlideProvider.providerType === 'ads') {
				const loopCount = this.state.visualContentLoopLoopPlayCount;
				const prevCounts = { ...this.state.visualContentLoopPlayedSlideCounts };
				const loopPlayed = prevCounts[loopCount] || {};
				const prevCount = loopPlayed[nextSlide.providerID] || 0;

				nextPlayedSlideCounts = {
					...prevCounts,
					[loopCount]: {
						...loopPlayed,
						[nextSlide.providerID]: prevCount + 1,
					},
				};
			}
		}

		let nextCurrSlideID = false;
		let nextNextSlideID = false;

		if (nextSlide) {
			nextCurrSlideID = nextSlide.slideID;
			// nextSlide is about to play; predict what follows it with the same
			// picker next() itself uses, in peek mode, so a folder successor is
			// pre-mounted for the crossfade. (DEV-12201)
			const nextNextSlide = this._predictSlideAfter(nextSlide, nextProviderAttempts, undefined, nextProviderSlideIndex);
			if (nextNextSlide) {
				nextNextSlideID = nextNextSlide.slideID;
			}
		}

		// Keep the outgoing slide mounted for the fade-out only when it
		// actually played; an errored/skipped slide should vanish at once.
		const retainPrev = !!(currSlide && nextSlide && !slidePlayInfo.error && !slidePlayInfo.skipped);
		const prevSlideID = retainPrev ? currSlide.slideID : false;

		const nextState = {
			currSlideID: nextCurrSlideID,
			nextSlideID: nextNextSlideID,
			prevSlideID,
			status:      {
				...this.state.status,
				slideLoopCount: 0,
			},
			providerPlays:      nextProviderPlays,
			providerAttempts:   nextProviderAttempts,
			providerSlideIndex: nextProviderSlideIndex,
		};

		if (nextPlayedSlideCounts) {
			nextState.visualContentLoopPlayedSlideCounts = nextPlayedSlideCounts;
		}


		const [ , playTime, ] = this.broodProofOfPlayEnd(currSlide);
		if (isVisualContentLoop) {
			if (slidePlayInfo.error || playTime < 950) { // < ~1s minimum play time
				const newErrorSlideCounts = { ...this.state.visualContentLoopErrorSlideCounts };
				if (typeof newErrorSlideCounts[this.state.visualContentLoopLoopPlayCount] === 'undefined') {
					newErrorSlideCounts[this.state.visualContentLoopLoopPlayCount] = 0;
				}
				newErrorSlideCounts[this.state.visualContentLoopLoopPlayCount] += 1;

				nextState.visualContentLoopErrorSlideCounts = newErrorSlideCounts;
			}
		}

		this.setState(nextState, () => {
			// Track when the new slide starts and reset pause timing
			this.currentSlideStartTime = Date.now();
			this.debugElapsed = 0;
			this.totalPausedTime = 0;
			this.pausedAt = null;

			if (slidePlayInfo.error || playTime < 950) { // < ~1s minimum play time
				// Note: addToBackoff for the error case ran earlier (top of
				// next()) so the picker / skipper see the updated state in
				// time. Don't re-add here or failureCount would double-tick
				// (1min → 2min backoff on a first failure).
				if (!slidePlayInfo.error) {
					this._afterNext(currSlide, isLastSlide);
				}

				// Release lock immediately for errors/skips
				this.isTransitioning = false;
				this.transitionStartTime = null;
				clearTimeout(this.safetyLockTimeout);
				if (this.state.prevSlideID) {
					this.setState({ prevSlideID: false });
				}
				return;
			}

			// Remove from backoff list if slide played successfully (only if it was in backoff)
			if (this.backoffSlides[`${currSlide.providerID || 'unknown'}_${currSlide.slideID}`]) {
				this.removeFromBackoff(currSlide);
			}

			clearTimeout(this.transitionTimer);
			this.transitionTimer = setTimeout(() => {
				this._afterNext(currSlide, isLastSlide);
				this.isTransitioning = false; // Release lock after afterNext is dispatched
				this.transitionStartTime = null;
				clearTimeout(this.safetyLockTimeout);
				// Fade-out finished: let the outgoing slide unmount if the
				// skipper rejects it. Guarded so a newer transition's prev
				// isn't clobbered (this timer is cleared on re-arm anyway).
				if (this.state.prevSlideID === prevSlideID) {
					this.setState({ prevSlideID: false });
				}
			}, transitionDuration); // matches CSS transition duration

			this.sendProofOfPlay(currSlide);
		});

		return;
	}

	hasNextSlide = () => {
		const {
			slides,
		} = this.state;

		if (slides.length <= 1) {
			return false;
		}

		const currSlide = this._currSlide(slides);
		const nextSlide = this._nextSlide(slides, currSlide);

		return currSlide !== nextSlide || (currSlide && nextSlide && currSlide.slideID !== nextSlide.slideID);
	}

	// peek (optional, e.g. { afterSlide }) is forwarded to the provider skipper
	// so it can answer "would this slide be playable once afterSlide has
	// played?" — used only to pre-compute nextSlideID for the crossfade.
	// Backoff/type checks below are always evaluated live. (DEV-12201)
	skipper = (slide, providersOverride, peek) => {
		const {
			is_visual_content_loop: isVisualContentLoop,
		} = this.props;

		const {
			slides,
			visualContentLoopLoopPlayCount,
			visualContentLoopMissingSlideCounts,
			visualContentLoopErrorSlideCounts,
		} = this.state;

		if (!slide) {
			return true;
		}

		// Check #0: unknown/missing slide type — never playable.
		// BroodSlide's render switch only handles image/video/html; anything
		// else falls through to its `default` branch which returns null and
		// can't start a duration timer. Without this guard the picker can
		// pick a bad slide (e.g. one whose upstream `kind` got persisted as
		// the response Content-Type of an S3 error like "text/xml") and the
		// deck black-screens until stuck-detection eventually fires ~20s later.
		// Treat it as unplayable so the picker passes it over immediately
		// and BroodSlide's `curr && skip` useEffect dispatches next() with
		// skipped:true if it somehow becomes current anyway.
		if (slide.type !== 'image' && slide.type !== 'video' && slide.type !== 'html') {
			return true;
		}

		// Check #1: Media backoff - skip slides that have failed recently
		if (this.isInBackoff(slide)) {
			return true;
		}

		if (isVisualContentLoop && slide.isBackfill) {
			// Never skip a backfill slide that's currently playing — provider updates
			// (e.g. Nova fetching new ads) can recalculate missing counts mid-play,
			// which would cause the BroodSlide useEffect to yank the slide via next().
			const { currSlideID } = this.state;
			if (slide.slideID === currSlideID) {
				return false;
			}

			let requiredBackfillPlays = this.nonBackfillSkipperCount();
			if (typeof visualContentLoopMissingSlideCounts[visualContentLoopLoopPlayCount] !== 'undefined') {
				for (const n of Object.values(visualContentLoopMissingSlideCounts[visualContentLoopLoopPlayCount])) {
					requiredBackfillPlays += n;
				}
			}
			if (typeof visualContentLoopErrorSlideCounts[visualContentLoopLoopPlayCount] !== 'undefined') {
				requiredBackfillPlays += visualContentLoopErrorSlideCounts[visualContentLoopLoopPlayCount];
			}

			if (requiredBackfillPlays <= 0) {
				return true;
			}

			// normal rules regarding scheduling and folder slots still apply
			const nonSkippedBackfillSlides = slides.filter((s) => {
				return s.isBackfill && !s.provider.skipper(s);
			});

			let n = 0, expectedFinalBackfillSlides = [];
			for (const s of nonSkippedBackfillSlides) {
				if (n < requiredBackfillPlays) {
					expectedFinalBackfillSlides.push(s);
				}

				n++;
			}
			
			if (expectedFinalBackfillSlides.length > 0) {
				let currentSlideIndex = expectedFinalBackfillSlides.findIndex((s) => {
					return s.slideID === slide.slideID;
				});

				// skip backfill not found in this loop
				if (currentSlideIndex === -1) {
					return true;
				}

				// expected backfill should not be skipped
				return false;
			}
		}

		const providerList = Array.isArray(providersOverride) ? providersOverride : this.state.providers;
		const provider = providerList.find((p) => {
			return p.providerID === slide.providerID;
		});

		// Check #2: Provider Skipper Logic (scheduling, etc.)
		const providerSkips = provider && provider.skipper
			&& (peek ? provider.skipper(slide, peek) : provider.skipper(slide));
		if (providerSkips) {
			return true;
		}

		if (isVisualContentLoop) {
			const {
				visualContentLoopProvidersRemoved,
			} = this.state;

			if (visualContentLoopProvidersRemoved.includes(slide.providerID)) {
				return true;
			}
		}

		return false; // Do not skip
	}

	nonBackfillSkipperCount = () => {
		const {
			slides,
		} = this.state;

		let count = 0;
		for (const slide of slides) {
			// Don't count folder slides - they're managed by folder windowing,
			// not backfill. Missing slots are handled by visualContentLoopMissingSlideCounts.
			if (!slide.isBackfill && !slide.folder_id && this.skipper(slide)) {
				count++;
			}
		}

		return count;
	}

	// Predict the slide that will play AFTER `slide` (which is current or about
	// to be), so it can be pre-mounted as `next` for the crossfade (DEV-12201).
	// Mirrors next()'s picker: same-provider unattempted-first pass with the
	// provider skipper evaluated in peek mode ("as if `slide` had played"),
	// then the provider-switch path when that provider would be exhausted, then
	// the plain index walk as a last resort. `attemptsByProvider` is the
	// providerAttempts map that will be in effect while `slide` plays.
	// A wrong prediction is not a correctness problem — the fallback fade in
	// BroodSlide still runs — it only costs the preload.
	_predictSlideAfter(slide, attemptsByProvider = {}, providersOverride, providerSlideIndex = {}) {
		if (!slide || !slide.slideID) {
			return null;
		}
		const providers = Array.isArray(providersOverride) ? providersOverride : this.state.providers;
		const allSlides = providers.flatMap((p) => { return p.slides || []; });
		const onlyProvider = this.isOnlyProviderWithSlides(slide.providerID, allSlides);
		const peek = {
			afterSlide: slide,
			// Pelican's pure-folder quota wrap happens in place only when the deck
			// is the sole provider; otherwise the deck yields to the next provider
			// at the round boundary (DEV-11981), so the wrap must not be simulated.
			allowWrap: onlyProvider,
		};

		const provider = providers.find((p) => { return p.providerID === slide.providerID; });
		if (provider && provider.slides && provider.slides.length > 0) {
			const attempted = new Set(attemptsByProvider[provider.providerID] || []);
			attempted.add(slide.slideID);
			const startIndex = provider.slides.findIndex((s) => { return s.slideID === slide.slideID; });
			const same = this._findNextPlayableSlide(provider, startIndex, attempted, peek, !onlyProvider, providersOverride);
			if (same) {
				return same.slide;
			}
			if (!onlyProvider) {
				const nextProvider = this._getNextProviderWithSlides(provider.providerID);
				if (nextProvider && nextProvider.providerID !== provider.providerID) {
					const saved = providerSlideIndex[nextProvider.providerID];
					const adjusted = (typeof saved === 'number' && saved >= 0) ? saved - 1 : -1;
					const switched = this._findNextPlayableSlide(nextProvider, adjusted, new Set(), peek, false, providersOverride);
					if (switched) {
						return switched.slide;
					}
				}
			}
		}

		const fallback = this._nextSlide(allSlides.length ? allSlides : this.state.slides, slide, providersOverride, peek);
		return (fallback && fallback.slideID !== slide.slideID) ? fallback : null;
	}

	// peek / firstPassOnly are used by _predictSlideAfter only (DEV-12201):
	// peek is forwarded to skipper(); firstPassOnly skips the second pass so a
	// provider with nothing unattempted left is reported as exhausted (the
	// real next() switches provider in that case instead of re-cycling).
	_findNextPlayableSlide(provider, startIndex = -1, attemptedSlides = new Set(), peek, firstPassOnly = false, providersOverride) {
		if (!provider || !provider.slides || provider.slides.length === 0) {
			return null;
		}

		const slides = provider.slides;

		// First pass: try to find a slide that hasn't been attempted yet and isn't skipped
		for (let i = 1; i <= slides.length; i++) {
			const index = (startIndex + i) % slides.length;
			const slide = slides[index];

			if (!this.skipper(slide, providersOverride, peek) && !attemptedSlides.has(slide.slideID)) {
				return {
					slide,
					index,
				};
			}
		}

		if (firstPassOnly) {
			return null;
		}

		// Second pass: if all slides have been attempted, find the next non-skipped slide
		// This handles the case where we've cycled through all slides but need to continue
		for (let i = 1; i <= slides.length; i++) {
			const index = (startIndex + i) % slides.length;
			const slide = slides[index];

			if (!this.skipper(slide, providersOverride, peek)) {
				return {
					slide,
					index,
				};
			}
		}

		return null; // No playable slide found in this provider
	}

	_getNextProviderWithSlides(currentProviderID) {
		const {
			providers,
		} = this.state;
		if (providers.length <= 1) {
			return providers.find((p) => {
				return p.providerID === currentProviderID;
			});
		}

		const startIndex = providers.findIndex((p) => {
			return p.providerID === currentProviderID;
		});

		for (let i = 1; i <= providers.length; i++) {
			const providerIndex = (startIndex + i) % providers.length;
			const provider = providers[providerIndex];

			if (provider.slides && provider.slides.length > 0) {
				const hasPlayableSlides = provider.slides.some((s) => {
					// Pass the current providerPlays so the skipper has the right context
					return !this.skipper(s);
				});
				if (hasPlayableSlides) {
					return provider;
				}
			}
		}
		return null; // No other providers have playable slides
	}

	isOnlyProviderWithSlides(providerID, slides = null) {
		const slidesToCheck = slides || this.state.slides;

		for (const slide of slidesToCheck) {
			if (slide.providerID !== providerID) {
				return false;
			}
		}

		return true;
	}

	broodProofOfPlayStart(slide) {
		if (!slide) {
			return;
		}

		// TODO: move from window to service worker for proper retries
		if (!window._broodProofOfPlay) {
			window._broodProofOfPlay = {};
		}

		console.log('BROOD::POP::START:', slide.slideID, slide.description);
		window._broodProofOfPlay[slide.slideID] = new Date();
	}

	broodProofOfPlayEnd(slide) {
		const now = new Date();

		console.log('BROOD::POP::END:', slide.slideID, slide.description);
		return [ now, now - window._broodProofOfPlay[slide.slideID], ];
	}

	// Merge provider PoP-response metrics into a /brood/pop body. Whitelisted
	// fields only (DEV-11958) — mutates body in place so a queue entry's
	// popBody snapshot picks the metrics up for verbatim replay.
	mergePopMetrics(body, popMetrics) {
		if (!body || !popMetrics) {
			return;
		}

		for (const field of broodPOPMetricFields) {
			if (popMetrics[field] !== undefined) {
				body[field] = popMetrics[field];
			}
		}
	}

	async sendBroodProofOfPlay(slide, popMetrics = null) {
		// A queued retry carries the exact body built on the original attempt —
		// resend it verbatim. Rebuilding from the trimmed queue entry loses the
		// resolved provider/provider_name (falls back to "SLIDE_DECK_<id>") and
		// recomputes duration from a stale start time. (DEV-11946)
		// Gated on isQueuedRetry: live slide objects persist across loop
		// rotations, so a leftover popBody from an earlier failed play must not
		// short-circuit a fresh play — each live play builds its own body.
		if (slide.isQueuedRetry && slide.popBody) {
			this.mergePopMetrics(slide.popBody, popMetrics);
			return this.postBroodProofOfPlay(slide.popBody);
		}

		const body = this.buildBroodProofOfPlayBody(slide);
		if (!body) {
			// no play-start marker was recorded for this slide — an error
			// prevented the play, or its slide component never called
			// broodProofOfPlayStart. Nothing to record; returning true so
			// any queued entry for it is removed
			return true;
		}

		this.mergePopMetrics(body, popMetrics);

		return this.postBroodProofOfPlay(body);
	}

	// Builds the /brood/pop body for a live play and snapshots it on the slide
	// (slide.popBody) for the retry queue. Returns null when the play never
	// happened. Extracted from sendBroodProofOfPlay so a deferred ad-play
	// record (provider fire failed, DEV-11958) can snapshot its play data
	// without posting yet.
	buildBroodProofOfPlayBody(slide) {
		if (!window._broodProofOfPlay[slide.slideID]) {
			// drop any stale snapshot so removal keys on the right play
			delete slide.popBody;
			return null;
		}

		const [ endOfPlay, playTime, ] = this.broodProofOfPlayEnd(slide);

		const {
			is_visual_content_loop: isVisualContentLoop,
			visual_content_loop_id: visualContentLoopID,
		} = this.props;

		let loopMode = 'vcl';
		if (isVisualContentLoop) {
			loopMode = 'vcl_plus';
		}

		let loopID = null;
		if (isVisualContentLoop) {
			loopID = visualContentLoopID || 0;
		}

		let loopName = this.props?.custom_content_sidebar_label || '';
		if (isVisualContentLoop) {
			loopName = this.state.visualContentLoopName || '';
		}

		let provider = slide.provider?.providerName || slide.provider?.constructor?.providerName || '';
		let providerName = slide.provider?.slideshowName || slide.provider?.props?.custom_content_sidebar_label || '';
		if (isVisualContentLoop) {
			providerName = slide.providerID.substring('PROVIDER_'.length, slide.providerID.length);

			if (provider === 'slide_deck') {
				providerName = slide.slideDeckName || slide.provider?.props?.provider?.name || '';
			}
			
			if (providerName.startsWith('INFOTAINMENT_')) {
				providerName = providerName.substring('INFOTAINMENT_'.length, slide.providerID.length).split('-')[0];
			}

			if (providerName.startsWith('ADVERTISING_')) {
				providerName = providerName.substring('ADVERTISING_'.length, slide.providerID.length).split('-')[0];

				// nova/peacock(vistar)
				if (slide.provider?.props?.venue_id) {
					providerName = slide.provider.props.venue_id;
				}
			}
		} else {
			if (slide.feed && slide.feed.name) {
				providerName = slide.feed.name.replace('monarchfeed', '');
			}
		}

		const body = {
			system_uuid:   this.props.system_uuid,
			asset_url:     slide.data.src,
			description:   slide.description || slide?.data?.alt || '',
			loop_mode:     loopMode,
			loop_id:	   loopID,
			loop_name:     loopName,
			provider:      provider,
			provider_name: providerName,
			duration:      (playTime),
			media_type:    slide.type,
			start_of_play: window._broodProofOfPlay[slide.slideID].toISOString(),
			end_of_play:   (new Date(endOfPlay)).toISOString(),
		};

		// snapshot for the retry queue (addToPoPQueue) so a failed send
		// replays with identical data
		slide.popBody = body;

		return body;
	}

	postBroodProofOfPlay(body) {
		let host = window._getEnv('BROOD_POP_URL');
		if (!host) {
			host = window._getEnv('BRONCO_URL');
		}

		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({
			event: 'Slideshow POP',
			...body,
		});

		return fetch(`${host}/brood/pop`, {
			'method':  'POST',
			'headers': {
				'Content-Type': 'application/json',
			},
			'body': JSON.stringify(body),
		})
			.then((response) => {
				if (response.ok) {
					console.log('BROOD::POP::SUCCESS:', body);
				}

				return response.ok;
			})
			.catch((error) => {
				console.error('BROOD::POP::ERROR:', error);
			});
	}

	addToPoPQueue(slide, shouldAddToProviderProofOfPlayQueue, shouldAddToBroodProofOfPlayQueue) {
		const {
			is_visual_content_loop: isVisualContentLoop,
		} = this.props;
		
		const {
			provider,
		} = slide;

		this.currentPoPQueue[slide.provider.providerID] = this.currentPoPQueue[slide.provider.providerID] || {};
		this.currentPoPQueue[this.broodID] = this.currentPoPQueue[this.broodID] || {};

		const trimmedSlide = {
			slideID:           slide.slideID,
			proof_of_play_url: slide.proof_of_play_url,
			// a re-queued failed retry keeps its ORIGINAL queueTime — resetting
			// it on every retry cycle defeated the max-age expiry (DEV-11946)
			queueTime:         slide.queueTime || Date.now(),
			type:              slide.type,
			description:       slide.description,
			// exact body of the failed brood send — replayed verbatim on retry,
			// but ONLY for queue entries (isQueuedRetry): a live slide keeps
			// playing and must build a fresh body per play (DEV-11946)
			isQueuedRetry:     true,
			popBody:           slide.popBody,
			data:              {
				src: slide.data.src,
			},
			provider: {
				providerID:  provider.providerID,
				constructor: {
					providerName: provider.constructor.providerName,
				},
			},
		};

		if (isVisualContentLoop) {
			trimmedSlide.providerID = slide.providerID;
			if (!trimmedSlide.provider) {
				trimmedSlide.provider = {};
			}
			trimmedSlide.provider.providerID = slide.providerID;
		}

		if (shouldAddToProviderProofOfPlayQueue) {
			this.currentPoPQueue[provider.providerID][slide.proof_of_play_url] = trimmedSlide;
		}

		if (shouldAddToBroodProofOfPlayQueue) {
			this.currentPoPQueue[this.broodID][this.broodPoPQueueKey(slide)] = trimmedSlide;
		}

		// Bound the queue at add time: during an outage on a VCL brood no
		// drain runs, so sendPoPQueue's expiry check alone would let the
		// queue grow for the whole outage (DEV-11946)
		this.prunePoPQueue();

		sessionStorage.setItem(broodPOPSessionKey, JSON.stringify(this.currentPoPQueue));
	}

	// Drop expired entries everywhere, then enforce the per-bucket cap by
	// evicting the oldest entries first (a bounded recent window is more
	// useful than an unbounded stale one)
	prunePoPQueue() {
		// A drain may be mid-flight over these same entries — pruning (and
		// flushing) concurrently could double-send a popBody the drain is
		// awaiting. The drain does its own expiry handling; prune again on
		// the next add (DEV-11958).
		if (this.isSendingPoPQueue) {
			return;
		}

		const cutoff = Date.now() - broodPOPQueueMaxAgeMs;

		for (const [ bucketKey, bucket, ] of Object.entries(this.currentPoPQueue)) {
			for (const [ key, entry, ] of Object.entries(bucket)) {
				if ((entry.queueTime || 0) < cutoff) {
					this.flushBroodPoPEntry(bucketKey, entry);
					delete bucket[key];
				}
			}

			const keys = Object.keys(bucket);
			if (keys.length > broodPOPQueueMaxEntries) {
				keys
					.sort((a, b) => {
						return (bucket[a].queueTime || 0) - (bucket[b].queueTime || 0);
					})
					.slice(0, keys.length - broodPOPQueueMaxEntries)
					.forEach((key) => {
						this.flushBroodPoPEntry(bucketKey, bucket[key]);
						delete bucket[key];
					});
			}
		}
	}

	// A brood-bucket entry is a play record that never successfully reached
	// /brood/pop (failed send or deferred behind a provider-fire retry). Never
	// drop one silently — fire one last best-effort flush of its popBody so
	// the play row lands metrics-less instead of vanishing (DEV-11958).
	// Fire-and-forget: prune is synchronous and the entry is dropped either
	// way; this is the row's last chance, not a retry loop.
	flushBroodPoPEntry(bucketKey, entry) {
		if (bucketKey !== this.broodID || !entry?.popBody) {
			return;
		}

		this.postBroodProofOfPlay(entry.popBody);
	}

	// Brood queue entries are keyed per PLAY, not per slide: keying by slideID
	// alone let a later successful live play of the same slide delete a queued
	// unsent play, and collapsed multiple failed plays into one entry (DEV-11946)
	broodPoPQueueKey(slide) {
		if (slide.popBody?.start_of_play) {
			return `${slide.slideID}::${slide.popBody.start_of_play}`;
		}

		return slide.slideID;
	}

	hasQueuedPoP() {
		return Object.values(this.currentPoPQueue).some((bucket) => {
			return Object.keys(bucket).length > 0;
		});
	}

	// True while a provider PoP fire for this play is still queued for retry —
	// its paired brood record is deferred until the fire resolves (DEV-11958)
	hasQueuedProviderPoP(proofOfPlayURL) {
		return Object.entries(this.currentPoPQueue).some(([ bucketKey, bucket, ]) => {
			return bucketKey !== this.broodID && !!bucket[proofOfPlayURL];
		});
	}

	removeFromPoPQueue(slide, removeFromProviderQueue, removeFromBroodQueue) {
		// provider entries are keyed by proof_of_play_url — a slide without
		// one (non-ad play) has no provider entry, and deleting with an
		// undefined key would remove whatever entry got stored under the
		// literal "undefined" key instead
		if (removeFromProviderQueue && slide.proof_of_play_url) {
			const {
				provider,
			} = slide;

			this.currentPoPQueue[provider.providerID] = this.currentPoPQueue[provider.providerID] || {};
			delete this.currentPoPQueue[provider.providerID][slide.proof_of_play_url];
		}

		if (removeFromBroodQueue) {
			this.currentPoPQueue[this.broodID] = this.currentPoPQueue[this.broodID] || {};
			delete this.currentPoPQueue[this.broodID][this.broodPoPQueueKey(slide)];
		}

		sessionStorage.setItem(broodPOPSessionKey, JSON.stringify(this.currentPoPQueue));
	}

	async sendProofOfPlay(slide, shouldSendToProvider = true, shouldSendToBrood = true) {
		const {
			is_visual_content_loop: isVisualContentLoop,
		} = this.props;

		let enableProofOfPlay = this.props.enable_proof_of_play;
		if (isVisualContentLoop) {
			enableProofOfPlay = true;
		}

		const {
			providers,
		} = this.state;

		let providerProofOfPlaySuccess = false;
		let broodProofOfPlaySuccess = false;

		let shouldAddToProviderProofOfPlayQueue = false;
		let shouldAddToBroodProofOfPlayQueue = false;

		const provider = providers.find((p) => {
			return p.providerID === slide.provider?.providerID;
		});

		// Vistar/Nova PoP-response metrics returned by the provider fire —
		// relayed into the /brood/pop record (DEV-11958)
		let popMetrics = null;

		if (shouldSendToProvider && provider?.providerProofOfPlay) {
			const res = await provider.providerProofOfPlay(slide);
			providerProofOfPlaySuccess = !!res;

			if (res && typeof res === 'object') {
				popMetrics = res;
			}
		}

		shouldAddToProviderProofOfPlayQueue = shouldSendToProvider && !providerProofOfPlaySuccess && !!provider?.providerProofOfPlay;

		// A queued provider retry just succeeded — its deferred brood record
		// (same play, same queue key) is waiting in the brood bucket. Send it
		// now with the fresh metrics instead of letting a later drain pass
		// send it metrics-less (DEV-11958).
		if (providerProofOfPlaySuccess && slide.isQueuedRetry && !shouldSendToBrood) {
			const pairedBroodEntry = this.currentPoPQueue[this.broodID]?.[this.broodPoPQueueKey(slide)];
			if (pairedBroodEntry) {
				const res = await this.sendBroodProofOfPlay(pairedBroodEntry, popMetrics);
				if (res) {
					this.removeFromPoPQueue(pairedBroodEntry, false, true);
				} else {
					// the metrics were merged into the entry's popBody —
					// persist them so a reload doesn't lose them
					sessionStorage.setItem(broodPOPSessionKey, JSON.stringify(this.currentPoPQueue));
				}
			}
		}

		// The provider fire failed for a live ad play: defer the brood record
		// (queue it unsent) so the provider retry can attach its metrics —
		// writing a metrics-less row now would be unfixable, rows are
		// insert-only (DEV-11958). Snapshot the play data for the deferred
		// send; a null body means the play never happened, nothing to record.
		let deferBroodForProviderRetry = false;
		if (shouldAddToProviderProofOfPlayQueue && shouldSendToBrood && enableProofOfPlay && !slide.isQueuedRetry) {
			deferBroodForProviderRetry = !!this.buildBroodProofOfPlayBody(slide);
		}

		if (shouldSendToBrood && enableProofOfPlay && !deferBroodForProviderRetry) {
			const res = await this.sendBroodProofOfPlay(slide, popMetrics);
			broodProofOfPlaySuccess = !!res;
		}

		// use the effective flag (VCL forces PoP on) — gating on the raw prop
		// silently dropped failed sends on VCL systems without it (DEV-11946)
		shouldAddToBroodProofOfPlayQueue = shouldSendToBrood && !broodProofOfPlaySuccess && enableProofOfPlay;

		if (shouldAddToProviderProofOfPlayQueue || shouldAddToBroodProofOfPlayQueue) {
			this.addToPoPQueue(slide, shouldAddToProviderProofOfPlayQueue, shouldAddToBroodProofOfPlayQueue);
		}

		if (providerProofOfPlaySuccess || broodProofOfPlaySuccess) {
			this.removeFromPoPQueue(slide, providerProofOfPlaySuccess, broodProofOfPlaySuccess);
		}

		// A successful live send proves the backend is reachable — drain any
		// queued failed plays now. The isLastSlide flush never fires on VCL
		// broods where a single provider holds all the slides, so without
		// this trigger queued plays were never replayed (DEV-11946).
		if (broodProofOfPlaySuccess && !slide.isQueuedRetry && this.hasQueuedPoP()) {
			this.sendPoPQueue();
		}
	}

	async sendPoPQueue() {
		// Guard against overlapping drains (e.g. the success-triggered drain
		// firing while a previous drain is mid-flight) double-sending entries
		if (this.isSendingPoPQueue) {
			return;
		}
		this.isSendingPoPQueue = true;

		console.log('BROOD::POP_QUEUE::START');

		try {
			for (const [ providerKey, providerData, ] of Object.entries(this.currentPoPQueue)) {
				for (const [ slideKey, slideData, ] of Object.entries(providerData)) {
					// a paired send earlier in this drain may have already
					// removed the entry (DEV-11958) — Object.entries is a
					// snapshot, so re-check before acting on it
					if (!this.currentPoPQueue[providerKey]?.[slideKey]) {
						continue;
					}

					if (slideData.queueTime + broodPOPQueueMaxAgeMs < Date.now()) {
						// flush the paired unsent brood record once before
						// dropping both sides — the play row must never be
						// lost; its NULL metrics are the "provider fire never
						// confirmed" signal. If the flush itself fails the
						// brood side stays queued so a later drain or the
						// add-time prune gets another attempt — the record
						// has never had a successful send (DEV-11958)
						let broodFlushed = true;
						const broodEntry = this.currentPoPQueue[this.broodID]?.[this.broodPoPQueueKey(slideData)];
						if (broodEntry?.popBody) {
							broodFlushed = !!(await this.postBroodProofOfPlay(broodEntry.popBody));
						}

						this.removeFromPoPQueue(slideData, true, broodFlushed);
					} else if (providerKey === this.broodID && slideData.proof_of_play_url && this.hasQueuedProviderPoP(slideData.proof_of_play_url)) {
						// deferred ad-play record whose provider fire is still
						// queued: let the provider retry attach its metrics
						// (pairing in sendProofOfPlay) or the expiry flush
						// send it, whichever comes first (DEV-11958)
						continue;
					} else {
						await this.sendProofOfPlay(slideData, providerKey !== this.broodID, providerKey === this.broodID);
					}
				}
			}
		} finally {
			this.isSendingPoPQueue = false;
		}

		console.log('BROOD::POP_QUEUE::END');
	}

	async fetchVisualContentLoopProviders() {
		const {
			is_visual_content_loop: isVisualContentLoop,
			visual_content_loop_id: visualContentLoopId,
		} = this.props;

		if (!isVisualContentLoop || !visualContentLoopId) {
			return;
		}

		try {
			const response = await fetch(`${broncoURLENV}/visualContentLoop/${visualContentLoopId}/providers`, {
				method:  'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					account_uuid:      this.props.account_uuid,
					system_group_uuid: this.props.system_group_uuid,
					system_uuid:       this.props.system_uuid,
				}),
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const visualContentLoopProvidersData = await response.json();

			const {
				loop_name: loopName,
				providers: visualContentLoopProviders,
			} = visualContentLoopProvidersData;

			const {
				providers,
				visualContentLoopProvidersRemoved,
			} = this.state;

			const nextVisualContentLoopProvidersRemoved = [ ...visualContentLoopProvidersRemoved, ];
			for (const provider of providers) {
				let found = false;
				for (const visualContentLoopProvider of Object.values(visualContentLoopProviders)) {
					let prefix;
					if (visualContentLoopProvider.type === 'slide_deck') {
						prefix = 'PROVIDER_SLIDE_DECK_';
					}
					if (visualContentLoopProvider.category) {
						prefix = `PROVIDER_${visualContentLoopProvider.category.toUpperCase()}_`;
					}

					if (provider.providerID.startsWith(`${prefix}${visualContentLoopProvider.id}`)) {
						found = true;
						break;
					}
				}

				if (!found) {
					nextVisualContentLoopProvidersRemoved.push(provider.providerID);
					continue;
				}
			}

			this.setState({
				visualContentLoopName: loopName,
				visualContentLoopProviders,
				visualContentLoopProvidersRemoved: nextVisualContentLoopProvidersRemoved,
			});
		} catch (error) {
			console.error('BROOD::FETCH_VISUAL_CONTENT_LOOP_PROVIDERS::ERROR:', error);
			// Set empty array on error to prevent UI issues
			this.setState({
				visualContentLoopProviders: [],
			});
		}
	}

	startDebugCountdownTimer() {
		if (this.debugCountdownTimer) return;

		this.debugCountdownTimer = setInterval(() => {
			const currSlide = this._currSlide(this.state.slides);

			if (currSlide && this.currentSlideStartTime) {
				const now = Date.now();
				let duration = currSlide.options?.duration || 5000;

				// For videos, try to get the real duration from the video element
				if (currSlide.type === 'video') {
					const videoElements = document.querySelectorAll('video[data-desc]');
					for (const video of videoElements) {
						const videoDesc = video.getAttribute('data-desc');
						if (videoDesc === currSlide.description && video.duration && !isNaN(video.duration)) {
							duration = video.duration * 1000; // Convert to milliseconds
							break;
						}
					}
				}

				if (this.playing) {
					// When playing: calculate elapsed time minus any paused time
					const rawElapsed = now - this.currentSlideStartTime;
					this.debugElapsed = rawElapsed - this.totalPausedTime;
					this.debugCountdown = Math.max(0, duration - this.debugElapsed);
					this.debugActualDuration = duration;

				} else if (this.pausedAt) {
					// When paused: elapsed time is frozen, show total paused time
					const rawElapsed = this.pausedAt - this.currentSlideStartTime;
					this.debugElapsed = rawElapsed - this.totalPausedTime;
					this.debugCountdown = Math.max(0, duration - this.debugElapsed);
					this.debugPausedTime = this.totalPausedTime + (now - this.pausedAt);
					this.debugActualDuration = duration;
				}
			}

			// Only update if debug overlay is visible to avoid unnecessary renders
			if (this.showDebugOverlay) {
				this.forceUpdate();
			}
		}, 500);
	}

	getStatus() {
		const {
			slides, status,
		} = this.state;
		const currSlide = this._currSlide(slides);
		const currentIndex = currSlide ? slides.findIndex((s) => {
			return s.slideID === currSlide.slideID;
		}) : -1;

		return {
			playing: status.playing,
			slides:  slides.length,
			currentIndex,
		};
	}

	getCurrentSlide() {
		return this._currSlide(this.state.slides);
	}

	countSkippedSlides(provider) {
		if (!provider || !provider.slides) return 0;
		return provider.slides.filter((slide) => {
			return this.skipper(slide);
		}).length;
	}

	renderDebugOverlay() {
		if (!this.showDebugOverlay) {
			return null;
		}

		const {
			providers = [],
		} = this.state;

		const currentSlide = this.getCurrentSlide();
		const engineStatus = this.getStatus();

		if (this.showDebugOverlay) {
			this.startDebugCountdownTimer();
		}

		const sortedProviders = [...providers].sort(this.providerSorter);

		// Get ALL providers from state (our registered providers)
		const allProviders = sortedProviders.map((provider, index) => {
			const letter = String.fromCharCode(65 + index); // A, B, C, D, E...
			const totalSlides = provider.slides?.length || 0;
			const skippedCount = this.countSkippedSlides(provider);
			const maxSlides = provider.options?.maxSlides || 'N/A';
			const playedSlides = this.state.providerPlays[provider.providerID] || new Set();
			const plays = playedSlides.size;

			// Get the human-readable provider name from the provider object
			let displayName = provider.providerName || 'Unknown';

			// Fallback to shortened UUID if no providerName
			if (!displayName || displayName === 'Unknown') {
				displayName = provider.providerID.replace('PROVIDER_', '').slice(0, 8);
			}

			return {
				letter,
				providerID:   provider.providerID,
				displayName,
				totalSlides,
				skippedCount,
				limit:        maxSlides,
				plays,
				isRegistered: true,
				is_backfill:  !!provider.is_backfill,
				order:        provider.order,
				slots:        provider.slots,
				providerType: provider.providerType,
			};
		});

		// Find current provider for highlighting
		const currentProviderIndex = currentSlide ? allProviders.findIndex((p) => {
			return p.providerID === currentSlide.providerID;
		}) : -1;

		return (
			<div style={{
				position:     'fixed',
				top:          '10px',
				right:        '10px',
				background:   'rgba(0,0,0,0.9)',
				color:        'white',
				padding:      '15px',
				borderRadius: '8px',
				fontFamily:   'monospace',
				fontSize:     '12px',
				maxWidth:     '500px',
				maxHeight:    '80vh',
				overflow:     'auto',
				zIndex:       9999,
			}}>
				<h4 style={{
					margin: '0 0 10px 0',
					color:  '#4CAF50',
				}}>🎭 Brood Debug</h4>

				<div style={{
					marginBottom: '10px',
				}}>
					<strong>Engine Status:</strong><br />
					Playing: {engineStatus.playing ? '▶️ YES' : '⏸️ PAUSED'}<br />
					Total Slides: {engineStatus.slides}<br />
					Current Position: {engineStatus.currentIndex >= 0 ? engineStatus.currentIndex + 1 : 'N/A'}
				</div>

				{currentSlide && (
					<div style={{
						marginBottom: '10px',
					}}>
						<strong>🎬 Current Slide:</strong><br />
						<div style={{
							fontSize:   '11px',
							marginLeft: '5px',
						}}>
							{currentSlide.description || currentSlide.slideID || 'No description'}
						</div>
						<div style={{
							fontSize:   '11px',
							marginLeft: '5px',
							color:      '#FFC107',
						}}>
							Type: {currentSlide.type} |
							{currentSlide.type === 'video' && this.debugActualDuration && this.debugActualDuration !== (currentSlide.options?.duration || 5000) ?
								` Actual: ${Math.floor(this.debugActualDuration / 1000)}s (detected)` :
								` Duration: ${Math.floor((this.debugActualDuration || currentSlide.options?.duration || 5000) / 1000)}s`}
							{!this.playing && <span style={{
								color: '#ff9800',
							}}> [PAUSED]</span>}
						</div>
						<div style={{
							fontSize:   '11px',
							marginLeft: '5px',
						}}>
							Provider: {currentSlide.providerID?.replace('PROVIDER_', '') || 'Unknown'}
						</div>
						{this.currentSlideStartTime && (
							<div style={{
								fontSize:   '11px',
								marginLeft: '5px',
							}}>
								⏱️ Elapsed: {Math.floor(this.debugElapsed / 1000)}s | Remaining: {Math.floor(this.debugCountdown / 1000)}s
								{this.debugPausedTime > 0 && (
									<span style={{
										color: '#999',
									}}>
										{' '}(paused: {Math.floor(this.debugPausedTime / 1000)}s total)
									</span>
								)}
								<div style={{
									width:        '200px',
									height:       '4px',
									background:   '#333',
									marginTop:    '2px',
									borderRadius: '2px',
								}}>
									<div style={{
										width:      `${Math.min(100, (this.debugElapsed / (this.debugActualDuration || currentSlide.options?.duration || 5000)) * 100)}%`,
										height:     '100%',
										background: !this.playing ? '#999' :
												   this.debugCountdown > 2000 ? '#4CAF50' :
												   this.debugCountdown > 1000 ? '#FFC107' : '#ff4444',
										borderRadius: '2px',
										transition:   'all 0.1s ease',
									}}></div>
								</div>
							</div>
						)}
					</div>
				)}

				<div style={{
					marginBottom: '10px',
				}}>
					<strong>📋 Providers ({allProviders.length}):</strong><br />
					{allProviders.map((provider, index) => {
						const isCurrentProvider = index === currentProviderIndex;
						let content = `${provider.letter} - ${provider.displayName}: ${provider.totalSlides} slides${provider.skippedCount > 0 ? `, ${provider.skippedCount} skipped` : ''}, limit=${provider.limit}, plays=${provider.plays}`;
						if (this.props.is_visual_content_loop) {
							content += `, slots=${provider.slots}, order=${provider.order}`;
							if (provider.providerType) content += `, type=${provider.providerType}`;
							if (provider.is_backfill) content += `, backfill`;
						}

						return (
							<div key={provider.letter} style={{
								fontSize:   '11px',
								marginLeft: '5px',
								color:      '#4CAF50',
								fontWeight: isCurrentProvider ? 'bold' : 'normal',
							}}>
								{isCurrentProvider ? <strong>{content}</strong> : content}
							</div>
						);
					})}
				</div>

				<div style={{
					marginBottom: '10px',
				}}>
					<strong>🔄 Provider Cycling:</strong><br />
					<div style={{
						fontSize:   '11px',
						marginLeft: '5px',
					}}>
						Successful Plays: {JSON.stringify(Object.fromEntries(
							Object.entries(this.state.providerPlays).map(([ k, v, ]) => {
								return [ k, Array.from(v || new Set()), ];
							})
						))}<br />
						All Attempts: {JSON.stringify(Object.fromEntries(
							Object.entries(this.state.providerAttempts).map(([ k, v, ]) => {
								return [ k, Array.from(v || new Set()), ];
							})
						))}<br />
						Indexes: {JSON.stringify(this.state.providerSlideIndex)}
					</div>
				</div>

				{Object.keys(this.backoffSlides).length > 0 && (
					<div style={{
						marginBottom: '10px',
					}}>
						<strong>⏳ Backoff List ({Object.keys(this.backoffSlides).length}):</strong><br />
						{Object.entries(this.backoffSlides).map(([ slideKey, backoffInfo, ]) => {
							const now = Date.now();
							const timeSinceBackoffStart = now - backoffInfo.lastFailure;
							const backoffDurationMs = (backoffInfo.backoffDurationMinutes || backoffInfo.backoffMinutes || 1) * 60 * 1000;
							const minutesRemaining = Math.ceil(Math.max(0, backoffDurationMs - timeSinceBackoffStart) / (60 * 1000));
							return (
								<div key={`${slideKey}_${new Date().getTime()}`} style={{
									fontSize:   '10px',
									marginLeft: '5px',
									color:      '#ff9800',
								}}>
									{backoffInfo.description || backoffInfo.slideID}: {backoffInfo.failureCount} failures, {minutesRemaining}min left
								</div>
							);
						})}
					</div>
				)}

				{this.props.is_visual_content_loop && (
					<div style={{
						marginBottom: '10px',
					}}>
						<strong>📊 VCL Backfill State:</strong><br />
						<div style={{
							fontSize:   '11px',
							marginLeft: '5px',
						}}>
							Loop: {this.state.visualContentLoopLoopPlayCount}<br />
							Missing Counts: {JSON.stringify(this.state.visualContentLoopMissingSlideCounts[this.state.visualContentLoopLoopPlayCount] || {})}<br />
							Played Counts (ads): {JSON.stringify(this.state.visualContentLoopPlayedSlideCounts[this.state.visualContentLoopLoopPlayCount] || {})}<br />
							Error Count: {this.state.visualContentLoopErrorSlideCounts[this.state.visualContentLoopLoopPlayCount] || 0}<br />
							Non-Backfill Skips: {this.nonBackfillSkipperCount()}<br />
							<span style={{
								color: '#FFC107',
							}}>
								Required Backfill Plays: {(() => {
									let n = this.nonBackfillSkipperCount();
									const missing = this.state.visualContentLoopMissingSlideCounts[this.state.visualContentLoopLoopPlayCount] || {};
									for (const v of Object.values(missing)) { n += v; }
									n += this.state.visualContentLoopErrorSlideCounts[this.state.visualContentLoopLoopPlayCount] || 0;
									return n;
								})()}
							</span>
						</div>
					</div>
				)}

				<div style={{
					fontSize: '10px',
					color:    '#999',
				}}>
					<strong>Keyboard Shortcuts:</strong><br />
					<span style={{
						color: '#4CAF50',
					}}>P: Play/Pause</span> | N: Next (Rapid OK) | T: Toggle Transitions<br />
					<span style={{
						color: '#FFC107',
					}}>Shift+D: Debug Overlay</span>
				</div>
			</div>
		);
	}

	renderVisualContentLoopProviders() {
		const {
			is_visual_content_loop: isVisualContentLoop,
			system_uuid: systemUUID,
			account_uuid,
			condor_prop_list: broodPropList,
		} = this.props;

		const {
			visualContentLoopProviders: providers,
		} = this.state;

		if (!isVisualContentLoop) {
			return null;
		}

		const monarchProps = (broodPropList || []).find(p => p.condor_render_name === 'Monarch');

		const sortedProviders = [...providers].sort(this.providerSorter);
		return sortedProviders.map((provider) => {
			if (provider.type === 'slide_deck' || provider.type === 'published_feed') {
				if (!provider.slides) {
					provider.slides = [];
				}

				// Published feeds (DEV-11955) are cross-account slide decks — same
				// Pelican rendering; the prefix matches the category-based prefix the
				// removal diff computes for non-slide_deck providers.
				const idPrefix = provider.type === 'published_feed' ? 'PROVIDER_PUBLISHED_FEED_' : 'PROVIDER_SLIDE_DECK_';

				return (
					<PelicanProviderComponent
						key={`${idPrefix}${provider.id}`}
						broodID={this.broodID}
						isVisualContentLoop={isVisualContentLoop}
						providerID={`${idPrefix}${provider.id}`}
						provider={provider}
						account_uuid={account_uuid}
						allow_audio={provider.settings[systemUUID]?.allow_audio || false}
						allow_urls={provider.settings[systemUUID]?.allow_urls || false}
						updateSlideshowProvider={this.updateProvider.bind(this)}
						removeSlideshowProvider={this.removeProvider.bind(this)}
						limit={provider.slots}
					/>
				);
			}

			if (!provider.category) {
				provider.category = '';
			}

			if (provider.category.toLowerCase() === 'advertising') {
				let ProviderComponent;
				// mocking is a fake setting on the real 'nova' provider now
				// (mock_nova_enabled), not a provider type of its own — there is
				// no mock_nova component_type row, so hydrateProviderSettings
				// can't resolve settings for one. Dispatching it here would mount
				// a settings-less Nova that fetches the REAL /nova route.
				if (provider.type === 'nova') {
					ProviderComponent = NovaProviderComponent;
				}
				if (provider.type === 'peacock' || provider.type === 'vistar') {
					ProviderComponent = PeacockProviderComponent;
				}
				// if (provider.type === 'broadsign_feed') {
				// 	ProviderComponent = BroadsignProviderComponent;
				// }

				if (ProviderComponent) {
					const propList = {
						...provider.settings[systemUUID] || {},
					};

					return (
						<ProviderComponent
							{...propList}
							key={`PROVIDER_ADVERTISING_${provider.id}`}
							broodID={this.broodID}
							isVisualContentLoop={isVisualContentLoop}
							providerID={`PROVIDER_ADVERTISING_${provider.id}`}
							provider={provider}
							updateSlideshowProvider={this.updateProvider.bind(this)}
							removeSlideshowProvider={this.removeProvider.bind(this)}
							limit={provider.slots}
						/>
					);
				}
			}

			if (provider.category.toLowerCase() === 'infotainment') {
				const condorPropList = [
					{
						...provider.settings[systemUUID] || {},
						condor_render_name: `monarch_feed_${provider.type}`.split('_').join('').toLowerCase(),
						monarch_limit:      provider.slots,
					},
				];

				return (
					<MonarchProviderComponent
						key={`PROVIDER_INFOTAINMENT_${provider.id}`}
						broodID={this.broodID}
						isVisualContentLoop={isVisualContentLoop}
						providerID={`PROVIDER_INFOTAINMENT_${provider.id}`}
						provider={provider}
						updateSlideshowProvider={this.updateProvider.bind(this)}
						removeSlideshowProvider={this.removeProvider.bind(this)}
						condor_prop_list={condorPropList}
						proxied={monarchProps?.proxied}
						limit={provider.slots}
					/>
				);
			}
		});
	}

	renderProviders() {
		const {
			is_visual_content_loop: isVisualContentLoop,
			condor_component_list: providerComponents,
		} = this.props;

		if (isVisualContentLoop) {
			return null;
		}

		if (providerComponents.length === 0) {
			return null;
		}

		// React 19 fix: Replace React.cloneElement with direct component rendering
		// Pass explicit callback props instead of slideshow={this} reference
		// This ensures slideshow engine always injects the correct broodID, providerID, and callbacks
		return (
			providerComponents.map((providerComponent) => {
				if (!React.isValidElement(providerComponent)) {
					return null;
				}

				const ProviderComponent = providerComponent.type;
				return (
					<ProviderComponent
						key={`PROVIDER_${providerComponent.props.component_name}`}
						{...providerComponent.props}
						broodID={this.broodID}
						providerID={`PROVIDER_${providerComponent.props.component_name}`}
						updateSlideshowProvider={this.updateProvider.bind(this)}
						removeSlideshowProvider={this.removeProvider.bind(this)}
					/>
				);
			})
		);
	}

	renderSlides() {
		const {
			status,
			transitionEnabled,
			transitionDuration,
			slides,
			slideshow,
			currSlideID,
			nextSlideID,
			prevSlideID,
		} = this.state;

		if (slides.length === 0) {
			return null;
		}

		if (this.waitingForExpectedFirstProvider) {
			return null;
		}

		let lastSlideID;
		return slides.map((slide) => {
			if (lastSlideID === slide.slideID) {
				return null;
			}
			lastSlideID = slide.slideID;

			let className = 'brood-slide new-brood-slide';
			if (slide.slideID === currSlideID) {
				className += ' new-brood-slide-curr';
			}
			if (slide.slideID === nextSlideID) {
				className += ' new-brood-slide-next';
			}

			if (transitionEnabled) {
				className += ' new-brood-slide-transition';
			}

			let curr = false;
			if (slide.slideID === currSlideID) {
				curr = true;
			}

			let next = false;
			if (slide.slideID === nextSlideID) {
				next = true;
			}

			if (slides.length === 1) {
				curr = true;
				next = false;
			}

			// Outgoing slide of the transition in flight (see prevSlideID).
			const prev = !curr && slide.slideID === prevSlideID;

			return (
				<BroodSlide
					key={slide.slideID}
					className={className}
					curr={curr}
					next={next}
					prev={prev}
					done={!curr && !next}
					skip={this.skipper(slide)}
					slide={slide}
					slideshow={this}
					status={status}
					transitionEnabled={transitionEnabled}
					transitionDuration={transitionDuration}
				/>
			);
		});
	}

	render() {
		let {
			CSSModules,
			condor_render_name,
			custom_classes,
			modifier_classes,
			theme_variation,
		} = this.props;

		let css = (CSSModules && CSSModules[condor_render_name]) || {};

		// Custom classes
		let customClasses = '';
		if (custom_classes && custom_classes.length > 0) {
			custom_classes.forEach((element) => {
				if ((/^([a-z_]|-[a-z_-])[a-z\d_-]*$/i).test(element)) {
					customClasses += ` ${element}`;
				}
			});
		}

		// Modifier classes
		let modifierClasses = '';
		if (modifier_classes) {
			modifier_classes.forEach((element) => {
				if ((/^([a-z_]|-[a-z_-])[a-z\d_-]*$/i).test(element)) {
					modifierClasses += ` ${element}`;
				}
			});
		}

		return (
			<>
				{this.renderProviders()}
				{this.renderVisualContentLoopProviders()}

				<div className={`${css.slideshowCont} slideshowCont ${customClasses} ${css[theme_variation]}`}>
					<div className={`${css.cont} ${modifierClasses} $`}>
						<div className="brood-container" data-test-id="broodContainer">
							{this.renderSlides()}
						</div>
					</div>
				</div>

				{this.renderDebugOverlay()}
			</>
		);
	}
}

function BroodSlide(props) {
	const {
		slide,
		slideshow,
		curr,
		next,
		prev,
		skip,
		className,
		transitionEnabled,
	} = props;

	// componentWillUnmount equivalent
	useEffect(() => {
		return () => {
			clearInterval(slideshow.skipCheckInterval);

			return;
		};
	}, [ slideshow, ]);

	useEffect(() => {
		let reason = '';
		if (curr && slideshow.skipper(slide)) {
			reason = 'provider_skipper';
		} else if (!slide.type) {
			reason = 'no_slide_type';
		}

		if (curr && !skip) {
			clearInterval(slideshow.skipCheckInterval);

			return;
		}

		if (curr && skip) {
			// Use a timeout to safely call next() after the current render cycle.
			const instantSkipTimer = setTimeout(() => {
				slideshow.next(slide, curr, {
					skipped: true,
					reason,
				});
			}, 1);

			if (slideshow.hasNextSlide()) {
				return () => {
					clearTimeout(instantSkipTimer);
					clearInterval(slideshow.skipCheckInterval);

					return;
				};
			}
			// not sure this is used or can even be hit anymore?
			// if we have no slides this might hit? if we have 1 slide the
			// slideshow.next() will be called from the slide
			clearInterval(slideshow.skipCheckInterval);
			slideshow.skipCheckInterval = setInterval(() => {
				slideshow.next(slide, curr, {
					skipped: true,
					reason,
				});
			}, 5000);


			return;
		}
	}, [ slide, slideshow, curr, skip, ]);

	// A skipped slide renders nothing — unless it is the `next` or `prev`
	// slide of a transition, which must stay mounted for the crossfade even
	// when the provider skipper rejects it (Pelican folder cursor: the
	// successor isn't "its turn" yet, the outgoing file no longer is).
	// `curr && skip` keeps its existing semantics (renders null, the effect
	// above dispatches next(skipped)). (DEV-12201)
	const retainedForTransition = !curr && (next || prev);
	const willRenderLeaf = !!slide.type && !(slideshow.skipper(slide) && !retainedForTransition);

	// Fresh-mount fade-in (DEV-12201). The crossfade relies on the incoming
	// slide already being in the DOM at opacity 0 (pre-mounted as `next`).
	// When the leaf mounts straight into `curr` (pre-mount missed — e.g. the
	// provider's predicted successor went into backoff meanwhile), withhold
	// the -curr class for two frames so the opacity transition still runs
	// instead of the slide popping in at full opacity. BroodSlide itself is
	// always mounted (renderSlides maps every slide), so "fresh" is tracked
	// on the LEAF: was it rendered by the previous commit?
	const leafMountedRef = useRef(false);
	const [ , bumpRender, ] = useState(0);
	const freshCurrMount = transitionEnabled && curr && willRenderLeaf && !leafMountedRef.current;
	useEffect(() => {
		const wasFresh = transitionEnabled && curr && willRenderLeaf && !leafMountedRef.current;
		leafMountedRef.current = willRenderLeaf;
		if (!wasFresh) {
			return undefined;
		}
		const raf = (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function')
			? window.requestAnimationFrame.bind(window)
			: (cb) => setTimeout(cb, 0);
		const caf = (typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function')
			? window.cancelAnimationFrame.bind(window)
			: clearTimeout;
		let second = null;
		// Two frames: the first commit must paint at opacity 0 before the
		// class flips, or the browser coalesces both styles into one frame.
		const first = raf(() => {
			second = raf(() => bumpRender((n) => n + 1));
		});
		return () => {
			caf(first);
			if (second !== null) {
				caf(second);
			}
		};
	}, [ willRenderLeaf, curr, transitionEnabled, ]);

	if (!willRenderLeaf) {
		return null; // Don't render anything for a skipped / typeless slide
	}

	let slideProps = props;
	if (freshCurrMount && typeof className === 'string') {
		slideProps = {
			...props,
			className: className.replace(/\s*\bnew-brood-slide-curr\b/, ''),
		};
	}

	switch (slide.type) {
	case 'image':
		return (
			<BroodImageSlide {...slideProps} />
		);

	case 'video':
		return (
			<BroodVideoSlide {...slideProps} />
		);

	case 'html':
		return (
			<BroodHTMLSlide {...slideProps} />
		);

	default:
		console.error('BROOD::ERROR::UNKNOWN_SLIDE_TYPE:', slide);
		// slideshow.next(slide, curr, {
		// 	skipped: true,
		// 	reason:  'unknown_slide_type',
		// });

		return null;
	}
}

export default BroodComponent;
